// @ts-nocheck
import { Component } from "@theme/component";
import { DialogComponent } from "@theme/dialog";
import Swiper from "@theme/swiper";

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "iframe",
  'input:not([disabled]):not([type="hidden"])',
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
  "details > summary",
].join(",");

const getFocusableElements = (container) => {
  return Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
    (el) =>
      el.offsetWidth > 0 ||
      el.offsetHeight > 0 ||
      el === document.activeElement,
  );
};

/** site header sticky 활성 시 높이 (sticky 시작점·앵커 scroll offset 공용) */
const getSiteHeaderHeight = () => {
  const header = document.querySelector("header-component");
  return header?.dataset.stickyState === "active"
    ? (header.getBoundingClientRect().height ?? 0)
    : 0;
};

/** site header + sticky tab bar 높이 (앵커 scroll · scroll-spy 공용) */
const getAnchorScrollOffset = (stickyInner = null) => {
  const stickyHeight = stickyInner?.getBoundingClientRect().height ?? 0;
  return getSiteHeaderHeight() + stickyHeight;
};

const focusScrollTarget = (target) => {
  if (!target) return;

  if (!target.matches(FOCUSABLE_SELECTOR) && !target.hasAttribute("tabindex")) {
    target.setAttribute("tabindex", "-1");
  }

  target.focus({ preventScroll: true });
};

/** 목표 갱신 임계값 — 미세 오차로 재이동이 튀어 보이지 않도록 */
const ANCHOR_SCROLL_TOLERANCE = 2;

/** 문서 끝 등으로 목표에 닿지 못할 때 루프를 끝낼 정지 프레임 수 */
const ANCHOR_SCROLL_STALL_FRAMES = 5;

let anchorScrollRafId = 0;

/**
 * scrollToAnchor
 * focusTarget: true only when the anchor's target should also receive focus, like an anchor-link navigation.
 */
const scrollToAnchor = ({
  anchorElement,
  stickyHeader = null,
  focusTarget = true,
  behavior = "smooth",
} = {}) => {
  if (!anchorElement) return;

  if (anchorScrollRafId) {
    cancelAnimationFrame(anchorScrollRafId);
    anchorScrollRafId = 0;
  }

  const getOffsetTop = () =>
    Math.max(
      0,
      window.scrollY +
        (anchorElement.getBoundingClientRect().top ?? 0) -
        getAnchorScrollOffset(stickyHeader),
    );

  let targetTop = getOffsetTop();

  if (Math.abs(window.scrollY - targetTop) <= ANCHOR_SCROLL_TOLERANCE) {
    if (focusTarget) focusScrollTarget(anchorElement);
    return;
  }

  window.scrollTo({ top: targetTop, behavior });

  let lastY = window.scrollY;
  let stalledFrames = 0;

  /**
   * 이동 중 header sticky 상태·앵커 위 레이아웃이 바뀌면 목표를 다시 잡는다.
   * 착지 후 한 번에 점프하는 대신 진행 중인 smooth 궤적에 흡수시키기 위함.
   */
  const retarget = () => {
    anchorScrollRafId = 0;
    const nextTop = getOffsetTop();

    if (Math.abs(nextTop - targetTop) > ANCHOR_SCROLL_TOLERANCE) {
      targetTop = nextTop;
      window.scrollTo({ top: targetTop, behavior });
    }

    const currentY = window.scrollY;
    stalledFrames = Math.abs(currentY - lastY) < 1 ? stalledFrames + 1 : 0;
    lastY = currentY;

    const reachedTarget =
      Math.abs(currentY - targetTop) <= ANCHOR_SCROLL_TOLERANCE;

    if (reachedTarget || stalledFrames >= ANCHOR_SCROLL_STALL_FRAMES) {
      const finalTop = getOffsetTop();
      if (Math.abs(window.scrollY - finalTop) > ANCHOR_SCROLL_TOLERANCE) {
        window.scrollTo({ top: finalTop, behavior: "auto" });
      }
      if (focusTarget) focusScrollTarget(anchorElement);
      return;
    }

    anchorScrollRafId = requestAnimationFrame(retarget);
  };

  anchorScrollRafId = requestAnimationFrame(retarget);
};

/** inline script · 커스텀 bind용 (type="module"은 전역 스코프 분리) */
window.scrollToAnchor = scrollToAnchor;

/**  handleScrollToAnchor */
function handleScrollToAnchor(e, params = {}) {
  const defaultParams = {
    targetElement: "a",
    stickyHeader: null,
  };

  const options = { ...defaultParams, ...params };
  const button =
    options.targetElement instanceof Element
      ? options.targetElement
      : e.target.closest(options.targetElement);

  if (button && button.hasAttribute("scroll-to-anchor")) {
    e.preventDefault();

    const anchorHref = button.getAttribute("href");
    const anchor =
      anchorHref?.indexOf("#") === 0 ? anchorHref?.split("#")[1] : null;
    const anchorElement = document.getElementById(anchor);
    if (!anchorElement) return;

    button.setAttribute("aria-current", "true");
    scrollToAnchor({
      anchorElement,
      stickyHeader: options.stickyHeader,
    });
  }
}

/**
 * handleFileDownload
 * a[file-download]
 * - Desktop: fetch → Blob → a[download]로 저장 강제
 * - Mobile: MOBILE_FILE_DOWNLOAD_MODE 로 동작 선택
 *   - 'new-tab'        : 강제 다운로드 없이 새 창 오픈
 *   - 'force-download' : fetch → Blob 강제 다운로드 (불가 시 Web Share / 새 창 폴백)
 */
async function handleFileDownload(e) {
  const link = e.target.closest("a[file-download]");
  if (!link) return;

  const href = link.getAttribute("href");
  if (
    !href ||
    href.startsWith("blob:") ||
    href.startsWith("data:") ||
    link.getAttribute("aria-disabled") === "true"
  ) {
    return;
  }

  const isIos =
    /iPad|iPhone|iPod/i.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const isMobile = isIos || /Android|Mobile/i.test(navigator.userAgent);

  e.preventDefault();

  // iOS: JS로 blob을 가로채면 download 속성이 무시되고 공유 시트로만 빠짐.
  // 실제 파일 URL로 새 탭을 열어, 서버의 Content-Disposition: attachment 헤더를 그대로 태워
  // Safari 네이티브 다운로드 배너를 유도.
  if (isIos) {
    window.open(href, "_blank", "noopener,noreferrer");
    return;
  }

  if (link.dataset.downloading === "true") return;

  const filename =
    link.getAttribute("download") ||
    href.split("/").pop()?.split("?")[0] ||
    "download";

  link.dataset.downloading = "true";
  link.setAttribute("aria-busy", "true");

  const triggerAnchorDownload = (url) => {
    const tempLink = document.createElement("a");
    tempLink.href = url;
    tempLink.download = filename;
    tempLink.rel = "noopener";
    document.body.appendChild(tempLink);
    tempLink.click();
    // Firefox: DOM 제거를 너무 빨리 하면 다운로드가 끊길 수 있음
    setTimeout(() => tempLink.remove(), 0);
  };

  // async 이후 window.open은 Firefox 팝업 차단에 걸리므로 a[target=_blank] 클릭 사용
  const openInNewTab = (url) => {
    const tempLink = document.createElement("a");
    tempLink.href = url;
    tempLink.target = "_blank";
    tempLink.rel = "noopener noreferrer";
    document.body.appendChild(tempLink);
    tempLink.click();
    tempLink.remove();
  };

  try {
    const response = await fetch(href, { mode: "cors", credentials: "omit" });
    if (!response.ok) throw new Error(`Download failed: ${response.status}`);

    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);

    // 데스크톱·Android: a[download]로 저장 시도
    triggerAnchorDownload(objectUrl);

    setTimeout(() => URL.revokeObjectURL(objectUrl), 30_000);
  } catch (error) {
    console.warn("[file-download]", error);

    try {
      const url = new URL(href, window.location.href);
      if (url.origin === window.location.origin && !isMobile) {
        triggerAnchorDownload(href);
        return;
      }
    } catch (_) {
      /* ignore URL parse errors */
    }

    openInNewTab(href);
  } finally {
    delete link.dataset.downloading;
    link.removeAttribute("aria-busy");
  }
}

document.addEventListener("click", (e) => {
  handleFileDownload(e);

  if (!e.target.closest("ui-tablist")) {
    handleScrollToAnchor(e);
  }
});

/**
 * Sticky scroll (.sticky-wrapper)
 *
 * DOM: .sticky-wrapper > .sticky-wrapper__inner (fixed 대상)
 * SCSS: .is-sticky / .is-past, min-height: var(--sticky-height)
 *
 * ── is-sticky 시작점 ──
 * top    : inner top이 header offset에 닿을 때
 * bottom : wrapper bottom이 header offset을 지날 때
 *
 * ── is-past ──
 * top    : 마지막 바운더리 bottom ≤ header + sticky bar
 * bottom : 마지막 바운더리 bottom ≤ header  OR  #footer-group이 viewport에 보일 때
 *
 * 바운더리: tablist → 마지막 panel / bind() getEndBoundary (함수 | 배열)
 */
const stickyScroll = (() => {
  const SELECTORS = {
    wrapper: ".sticky-wrapper",
    inner: ".sticky-wrapper__inner",
    tablist: 'ui-tablist, [role="tablist"], [role="navigation"]',
    tab: '[role="tab"], [scroll-to-anchor]',
  };

  const CLASSES = {
    sticky: "is-sticky",
    past: "is-past",
  };

  const STICKY_POSITION = {
    top: "top",
    bottom: "bottom",
  };

  const STICKY_MODE = {
    always: "always",
  };

  const FOOTER_BOUNDARY_SELECTOR = "#footer-group .footer__wrap";

  const CSS_VAR_HEIGHT = "--sticky-height";

  // wrapper element → controller (중복 bind 방지)
  const registry = new WeakMap();

  // wrapper 없을 때 반환용 no-op
  const noopController = {
    bind: () => {},
    destroy: () => {},
    update: () => {},
    refresh: () => {},
  };

  // ── 바운더리: tab → panel id ──

  /** tab의 href(#id) → panel id */
  const getTabPanelId = (tab) => {
    if (!tab) return null;
    const href = tab.getAttribute("href");
    if (href?.startsWith("#")) return href.slice(1) || null;

    return null;
  };

  /** tablist 내 tab 순서대로 panel Element 수집 */
  const getTabPanels = (wrapper) => {
    const tablist = wrapper?.querySelector(SELECTORS.tablist);
    if (!tablist) return [];

    const panels = [];

    tablist.querySelectorAll(SELECTORS.tab).forEach((tab) => {
      const panelId = getTabPanelId(tab);
      if (!panelId) return;

      const panel = document.getElementById(panelId);
      if (panel) panels.push(panel);
    });

    return panels;
  };

  /** Element | '#id' | selector → Element */
  const resolveBoundaryTarget = (target) => {
    if (!target) return null;
    if (target instanceof Element) return target;

    if (typeof target === "string") {
      return target.startsWith("#")
        ? document.getElementById(target.slice(1))
        : document.querySelector(target);
    }

    return null;
  };

  /** 배열 뒤에서부터 탐색 — 마지막 유효 boundary element 반환 */
  const getLastBoundary = (list) => {
    if (!Array.isArray(list) || list.length === 0) return null;

    for (let i = list.length - 1; i >= 0; i--) {
      const el = resolveBoundaryTarget(list[i]);
      if (el) return el;
    }

    return null;
  };

  // ── options / DOM 탐색 ──

  /** bind·create 공통 기본값 병합 */
  const withDefaults = (options = {}) => ({
    wrapperSelector: SELECTORS.wrapper,
    innerSelector: SELECTORS.inner,
    stickyClass: CLASSES.sticky,
    pastClass: CLASSES.past,
    heightVar: CSS_VAR_HEIGHT,
    getHeaderOffset: getSiteHeaderHeight,
    ...options,
  });

  /** getEndBoundary 옵션 → scroll 시 호출할 getter 함수로 변환 */
  const createEndBoundaryGetter = (wrapper, getEndBoundary) => {
    if (typeof getEndBoundary === "function") {
      return () => {
        const result = getEndBoundary();
        return Array.isArray(result) ? getLastBoundary(result) : result;
      };
    }

    if (Array.isArray(getEndBoundary)) {
      return () => getLastBoundary(getEndBoundary);
    }

    if (wrapper?.querySelector(SELECTORS.tablist)) {
      return () => getLastBoundary(getTabPanels(wrapper));
    }

    return undefined;
  };

  /** scope 또는 wrapper element → .sticky-wrapper 목록 */
  const collectWrappers = (scope = document) => {
    if (scope instanceof Element && scope.matches(SELECTORS.wrapper)) {
      return [scope];
    }

    if (
      scope instanceof Element ||
      scope instanceof Document ||
      scope instanceof DocumentFragment
    ) {
      return [...scope.querySelectorAll(SELECTORS.wrapper)];
    }

    return [];
  };

  /** shopify:section:* 이벤트 → querySelectorAll 가능한 section scope */
  const getShopifySectionScope = (event) => {
    if (event.target instanceof Element) {
      return event.target;
    }

    const sectionId = event.detail?.sectionId;
    if (sectionId) {
      return document.getElementById(`shopify-section-${sectionId}`);
    }

    return null;
  };

  /** el에서 가장 가까운 .sticky-wrapper */
  const findWrapper = (el, options = {}) => {
    const { wrapperSelector } = withDefaults(options);
    return el?.closest(wrapperSelector) ?? null;
  };

  /** wrapper 내부 fixed 대상(.sticky-wrapper__inner) */
  const findInner = (wrapper, options = {}) => {
    if (!wrapper) return null;

    const opts = withDefaults(options);

    if (opts.innerEl instanceof Element) return opts.innerEl;
    if (typeof opts.getInnerEl === "function") return opts.getInnerEl(wrapper);

    return wrapper.querySelector(opts.innerSelector);
  };

  /** scroll-to-anchor offset용 — el 기준 sticky inner bar */
  const getStickyInner = (el, options = {}) => {
    const opts = withDefaults(options);
    return findInner(findWrapper(el, opts), opts);
  };

  // ── 단일 wrapper controller ──

  /** scroll/resize 리스너 + is-sticky / is-past 상태 관리 */
  const createController = ({
    rootEl: wrapper,
    getEndBoundary,
    ...options
  } = {}) => {
    if (!wrapper) return noopController;

    const opts = withDefaults(options);
    let stickyStartY = null;
    let rafId = null;
    let abortController = null;

    const getInnerEl = () => findInner(wrapper, opts);

    const isBottomPosition = () =>
      wrapper.dataset.stickyPosition === STICKY_POSITION.bottom;

    const isAlwaysMode = () =>
      wrapper.dataset.stickyMode === STICKY_MODE.always;

    /** position-bottom은 하단 fixed — placeholder 높이 불필요 */
    const syncPlaceholderHeight = () => {
      if (isBottomPosition()) return;

      const height = getInnerEl()?.offsetHeight;
      if (height) wrapper.style.setProperty(opts.heightVar, `${height}px`);
    };

    const getHeaderOffset = () => opts.getHeaderOffset();

    /** is-past 판정용 offset — top: header+sticky / bottom: header */
    const getPastBoundaryOffset = () =>
      isBottomPosition()
        ? getHeaderOffset()
        : getAnchorScrollOffset(getInnerEl());

    /** sticky 시작 scrollY — always 모드는 시작점 없음 */
    const recalcStickyStartY = (force = false) => {
      if (isAlwaysMode()) {
        stickyStartY = null;
        return;
      }

      if (!force && wrapper.classList.contains(opts.stickyClass)) return;

      const innerEl = getInnerEl();
      if (!innerEl) {
        stickyStartY = null;
        return;
      }

      const rect = isBottomPosition()
        ? wrapper.getBoundingClientRect()
        : innerEl.getBoundingClientRect();
      const edge = isBottomPosition() ? rect.bottom : rect.top;

      stickyStartY = window.scrollY + edge - getHeaderOffset();
    };

    /** scrollY가 sticky 시작점을 지났는지 (미측정·scrollY=0 오적용 방지) */
    const isPastStickyStart = () => {
      if (isAlwaysMode()) return true;

      if (stickyStartY === null) return false;

      if (window.scrollY === 0 && stickyStartY <= 0) return false;

      return window.scrollY >= stickyStartY;
    };

    const isPastPanelBoundary = () => {
      if (typeof getEndBoundary !== "function") return false;

      const boundary = getEndBoundary();
      if (!boundary) return false;

      return boundary.getBoundingClientRect().bottom <= getPastBoundaryOffset();
    };

    const isInFooterZone = () => {
      if (!isBottomPosition()) return false;

      const footer = document.querySelector(FOOTER_BOUNDARY_SELECTOR);
      if (!footer) return false;

      const { top, bottom } = footer.getBoundingClientRect();
      return top < window.innerHeight && bottom > 0;
    };

    const isPastEndBoundary = () => isPastPanelBoundary() || isInFooterZone();

    /** scrollY 기준 is-sticky / is-past class 토글 */
    const applyStickyState = () => {
      rafId = null;
      const wasSticky = wrapper.classList.contains(opts.stickyClass);

      if (!wrapper.classList.contains(opts.stickyClass)) {
        syncPlaceholderHeight();
        if (!isAlwaysMode()) recalcStickyStartY(true);
      }

      if (!isPastStickyStart()) {
        wrapper.classList.remove(opts.stickyClass, opts.pastClass);
        return;
      }

      wrapper.classList.add(opts.stickyClass);
      wrapper.classList.toggle(opts.pastClass, isPastEndBoundary());

      if (!wasSticky) syncPlaceholderHeight();
    };

    /** scroll/resize burst 방지용 rAF 스케줄 */
    const scheduleApply = () => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(applyStickyState);
    };

    /** 리스너 등록 + 초기 상태 반영 */
    const bind = () => {
      destroy();
      wrapper.classList.remove(opts.stickyClass, opts.pastClass);

      abortController = new AbortController();
      const { signal } = abortController;

      const onScrollOrResize = () => {
        if (!isAlwaysMode() && !wrapper.classList.contains(opts.stickyClass)) {
          recalcStickyStartY();
        }
        scheduleApply();
      };

      syncPlaceholderHeight();
      recalcStickyStartY();
      scheduleApply();

      window.addEventListener("scroll", onScrollOrResize, {
        signal,
        passive: true,
      });
      window.addEventListener("resize", onScrollOrResize, {
        signal,
        passive: true,
      });
    };

    /** 리스너 해제 + class / CSS 변수 초기화 */
    const destroy = () => {
      abortController?.abort();
      abortController = null;

      if (rafId !== null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }

      wrapper.classList.remove(opts.stickyClass, opts.pastClass);
      wrapper.style.removeProperty(opts.heightVar);
      stickyStartY = null;
    };

    /** class 임시 해제 후 remeasure — 로드·스크롤 복원·섹션 reload 시 */
    const refresh = () => {
      wrapper.classList.remove(opts.stickyClass, opts.pastClass);
      syncPlaceholderHeight();
      recalcStickyStartY(true);
      scheduleApply();
    };

    return {
      bind,
      destroy,
      update: scheduleApply,
      measure: recalcStickyStartY,
      refresh,
    };
  };

  // ── registry API ──

  /**
   * wrapper bind + registry 등록
   */
  const bind = (wrapper, options = {}) => {
    if (!wrapper) return noopController;

    const { autoBind = true, getEndBoundary, ...rest } = options;

    const controller = createController({
      rootEl: wrapper,
      wrapperSelector: SELECTORS.wrapper,
      innerSelector: SELECTORS.inner,
      ...rest,
      getEndBoundary: createEndBoundaryGetter(wrapper, getEndBoundary),
    });

    registry.get(wrapper)?.destroy();
    registry.set(wrapper, controller);

    if (autoBind) controller.bind();

    return controller;
  };

  /** scope 내 .sticky-wrapper 일괄 bind (미등록만) */
  const init = (scope = document) => {
    collectWrappers(scope).forEach((wrapper) => {
      if (registry.has(wrapper)) return;
      bind(wrapper);
    });
  };

  /** scope 내 등록된 wrapper refresh */
  const refresh = (scope = document) => {
    collectWrappers(scope).forEach((wrapper) => {
      registry.get(wrapper)?.refresh();
    });
  };

  /** scope 또는 wrapper element destroy + registry 정리 */
  const destroy = (scope = document) => {
    collectWrappers(scope).forEach((wrapper) => {
      registry.get(wrapper)?.destroy();
      registry.delete(wrapper);
    });
  };

  /** init → rAF×2 refresh (swiper·font 등 레이아웃 확정 후 초기 상태 반영) */
  const boot = (scope = document) => {
    init(scope);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => refresh(scope));
    });
  };

  // ── 자동 init ──
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => boot());
  } else {
    boot();
  }

  window.addEventListener("load", () => refresh());
  window.addEventListener("pageshow", () => refresh());

  document.addEventListener("shopify:section:load", (event) => {
    const scope = getShopifySectionScope(event);
    if (scope) boot(scope);
  });
  document.addEventListener("shopify:section:unload", (event) => {
    const scope = getShopifySectionScope(event);
    if (scope) destroy(scope);
  });

  return {
    SELECTORS,
    CLASSES,
    STICKY_POSITION,
    STICKY_MODE,
    getTabPanelId,
    getTabPanels,
    getLastBoundary,
    collectWrappers,
    findWrapper,
    findInner,
    getStickyInner,
    getHeaderForElement: getStickyInner,
    createController,
    bind,
    init,
    refresh,
    destroy,
    boot,
  };
})();

/** inline script · 커스텀 bind용 (type="module"은 전역 스코프 분리) */
window.stickyScroll = stickyScroll;

/**
 * swiper 컴포넌트 공통 클래스
 */
class SwiperComponent extends HTMLElement {
  #selector = {
    pagination: ".swiper-pagination",
    prev: ".swiper-button-prev",
    next: ".swiper-button-next",
  };

  swiper = null;
  paginationEl = null;
  prevEl = null;
  nextEl = null;

  connectedCallback() {
    this.paginationEl = this.querySelector(this.#selector.pagination);
    this.prevEl = this.querySelector(this.#selector.prev);
    this.nextEl = this.querySelector(this.#selector.next);

    this.initSwiper();
  }

  disconnectedCallback() {
    this.destroySwiper();
  }

  initSwiper() {
    const swiperEl = this.classList.contains("swiper")
      ? this
      : this.querySelector(".swiper");
    if (!swiperEl) return;

    this.swiper = new Swiper(swiperEl, {
      ...this.setOptions(),
    });
  }

  destroySwiper() {
    this.swiper?.destroy(true, true);
    this.swiper = null;
  }

  setOptions() {
    return {
      pagination: {
        el: this.paginationEl,
        clickable: true,
      },
      navigation: {
        prevEl: this.prevEl,
        nextEl: this.nextEl,
      },
      threshold: 20,
    };
  }
}

/**
 * <ui-tablist>
 *
 * anchor mode (data-mode="scroll-to-anchor"): 스크롤 시 패널 진입점마다 탭 활성화 (scroll-spy)
 *
 * 외부에서 탭 활성화:
 * @example
 * const tablist = document.querySelector('ui-tablist');
 * tablist.activateTab(0); // 0-based index
 */
class UiTablist extends HTMLElement {
  #controller = null;
  #scrollSpyRafId = null;

  selector = {
    tab: '[role="tab"], [scroll-to-anchor]',
    panel: '[role="tabpanel"], .tabpanel',
  };

  tabs = [];
  panels = [];
  activeIndex = null;
  swiper = null;

  get isScroll() {
    return this.dataset.scroll === "true";
  }

  get scrollNavigationIcon() {
    return this.dataset.scrollNavigationIcon || "";
  }

  get isTopDownMode() {
    return this.dataset.mode === "top-down";
  }

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.tabs = this.querySelectorAll(this.selector.tab);
    if (this.tabs.length === 0) return;

    this.#syncPanels();

    if (this.isScroll) {
      this.#initSwiper();
    }

    if (this.isTopDownMode) {
      this.#initScrollSpy(signal);
    }

    this.activateTab();
    this.addEventListener("click", this.#onClick, { signal });
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#scrollSpyRafId = null;

    if (this.isScroll) {
      this.#destroySwiper();
    }
  }

  // 탭과 일치하는 패널 배열 동기화
  #syncPanels() {
    this.panels = [];

    this.tabs.forEach((tab) => {
      let panel = null;

      if (this.isTopDownMode) {
        const anchorHref = tab.getAttribute("href");
        const anchorId =
          anchorHref?.indexOf("#") === 0 ? anchorHref?.split("#")[1] : null;
        panel = document.getElementById(anchorId);
      } else {
        panel = document.getElementById(tab.getAttribute("aria-controls"));
      }
      this.panels.push(panel);
    });
  }

  // 탭 클릭 이벤트 핸들러
  #onClick = (e) => {
    const tab = e.target.closest(this.selector.tab);
    if (!tab) return;

    this.activeIndex = [...this.tabs].indexOf(tab);
    if (this.activeIndex === -1) return;

    this.activateTab(this.activeIndex);

    if (this.isTopDownMode) {
      handleScrollToAnchor(e, {
        targetElement: tab,
        stickyHeader: stickyScroll.getStickyInner(this),
      });
    }
  };

  // 탭 초기화 (비활성화)
  #resetTabs() {
    this.#syncPanels();

    this.tabs.forEach((tab, i) => {
      if (this.isTopDownMode) {
        tab.setAttribute("aria-current", "false");
        this.panels[i] && (this.panels[i].hidden = false);
      } else {
        tab.setAttribute("aria-selected", "false");
        this.panels[i] && (this.panels[i].hidden = true);
      }
    });
  }

  #initSwiper() {
    // swiper 레이아웃 생성
    if (!this.querySelector(".swiper-wrapper")) {
      const wrapper = document.createElement("div");
      wrapper.className = "swiper-wrapper";

      const navigationHTML = `
        <div class="swiper-button-prev">${this.scrollNavigationIcon}</div>
        <div class="swiper-button-next">${this.scrollNavigationIcon}</div>
      `;

      const tabs = this.querySelectorAll("button, a");
      tabs.forEach((tab) => {
        const slide = document.createElement("div");
        slide.className = "swiper-slide";
        slide.appendChild(tab);
        wrapper.appendChild(slide);
      });

      this.appendChild(wrapper);
      this.insertAdjacentHTML("beforeend", navigationHTML);
      this.classList.add("swiper");
    }

    // init
    this.swiper = new Swiper(this, {
      slidesPerView: "auto",
      freeMode: true,
      focusableElements: "input, select, option, textarea, video, label",
      navigation: {
        prevEl: this.querySelector(".swiper-button-prev"),
        nextEl: this.querySelector(".swiper-button-next"),
      },
      on: {
        init: (swiper) => {
          swiper.slideTo(this.activeIndex);
        },
      },
    });
  }

  #destroySwiper() {
    this.swiper?.destroy(true, true);
    this.swiper = null;
  }

  #getPrevButtonOffset() {
    const prevEl = this.querySelector(".swiper-button-prev");
    if (!prevEl) return 0;

    if (prevEl.offsetWidth > 0) return prevEl.offsetWidth;

    const width = parseFloat(getComputedStyle(prevEl).width);
    return Number.isFinite(width) ? width : 0;
  }

  #scrollToActive() {
    if (!this.swiper) return;

    // Left-align active tab; inset by prev button width so it sits beside it
    const willPrevBeVisible = this.activeIndex > 0 && !this.swiper.isLocked;

    if (willPrevBeVisible && this.swiper.slidesGrid?.length) {
      const slidePos = this.swiper.slidesGrid[this.activeIndex] ?? 0;
      const offset = this.#getPrevButtonOffset();
      const target = -(slidePos - offset);
      const clamped = Math.min(0, Math.max(this.swiper.maxTranslate(), target));
      this.swiper.translateTo(clamped, 300);
    } else {
      this.swiper.slideTo(this.activeIndex);
    }
  }
  /** scroll-spy: 패널 진입점(scroll offset) 기준 활성 탭 index */
  #getActiveIndexFromScroll() {
    // smooth scroll이 목표보다 1~3px 짧게 멈추는 브라우저 오차 보정
    const SCROLL_SPY_EPSILON = 3;
    const scrollLine =
      window.scrollY +
      getAnchorScrollOffset(stickyScroll.getStickyInner(this)) +
      SCROLL_SPY_EPSILON;
    let activeIndex = -1;

    this.panels.forEach((panel, i) => {
      if (!panel || this.tabs[i]?.getAttribute("aria-disabled") === "true")
        return;

      const panelTop = panel.getBoundingClientRect().top + window.scrollY;
      if (scrollLine >= panelTop) activeIndex = i;
    });

    if (activeIndex !== -1) return activeIndex;

    return [...this.tabs].findIndex(
      (tab, i) =>
        tab.getAttribute("aria-disabled") !== "true" && this.panels[i],
    );
  }

  #updateActiveTabFromScroll() {
    const index = this.#getActiveIndexFromScroll();
    if (index !== -1 && index !== this.activeIndex) {
      this.activateTab(index);
    }
  }

  #onScrollSpy = () => {
    if (this.#scrollSpyRafId !== null) return;
    this.#scrollSpyRafId = requestAnimationFrame(() => {
      this.#scrollSpyRafId = null;
      this.#updateActiveTabFromScroll();
    });
  };

  /** anchor mode — scroll/resize 시 패널 진입점마다 탭 활성화 */
  #initScrollSpy(signal) {
    window.addEventListener("scroll", this.#onScrollSpy, {
      signal,
      passive: true,
    });
    window.addEventListener("resize", this.#onScrollSpy, {
      signal,
      passive: true,
    });
    this.#onScrollSpy();
  }

  // 탭 활성화
  activateTab = (n = null) => {
    this.activeIndex =
      n ??
      [...this.tabs].findIndex((tab) => {
        if (this.isTopDownMode) {
          return tab.getAttribute("aria-current") === "true";
        } else {
          return tab.getAttribute("aria-selected") === "true";
        }
      });
    if (this.activeIndex === -1) return;

    const activeTab = this.tabs[this.activeIndex];
    const activePanelId = () => {
      if (this.isTopDownMode) {
        const activeHref = activeTab?.getAttribute("href");
        return activeHref?.indexOf("#") === 0
          ? activeHref?.split("#")[1]
          : null;
      } else {
        return activeTab?.getAttribute("aria-controls");
      }
    };

    const activePanel =
      this.panels.find((panel) => panel?.id === activePanelId()) ?? null;
    if (activePanel) {
      this.#resetTabs();
      activePanel.hidden = false;

      if (this.isTopDownMode) {
        activeTab.setAttribute("aria-current", "true");
      } else {
        activeTab.setAttribute("aria-selected", "true");
      }
    }

    if (this.swiper) {
      this.#scrollToActive();
    }
  };
}

if (!customElements.get("ui-tablist")) {
  customElements.define("ui-tablist", UiTablist);
}

/**
 * <ui-tablist-group>
 */
class UiTablistGroup extends UiTablist {
  #tablistGroupKey = null;
  #tabBlockKey = null;
  #tablist = null;

  get tablistMainKey() {
    return this.dataset.blockTablistKey;
  }

  get tablistSubKey() {
    return this.dataset.blockTablistSubkey;
  }

  get isTopDownMode() {
    return this.#tablist?.dataset.mode === "top-down";
  }

  connectedCallback() {
    this.#tablist = this.querySelector("ui-tablist");
    this.#syncTabGroupIds();

    requestAnimationFrame(() => {
      this.#syncNoMatchPanelTabs();
      this.swiper = this.#tablist?.swiper ?? null;
      super.connectedCallback();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  // 탭 블록용 id, aria-controls, aria-labelledby 등 연결 속성 자동으로 생성
  #syncTabGroupIds() {
    this.tabs = this.#tablist.querySelectorAll(this.selector.tab);
    this.#tablistGroupKey = this.tablistMainKey;
    if (!this.#tablistGroupKey) return;

    const matchedPanels = [
      ...document.querySelectorAll(this.selector.panel),
    ].filter(
      (panel) => panel.dataset.blockTabpanelKey === this.#tablistGroupKey,
    );

    const suffix = `${this.#tablistGroupKey}-${this.tablistSubKey}`;
    this.tabs.forEach((tab, i) => {
      this.#tabBlockKey = `${suffix}-${i + 1}`;

      const panel = matchedPanels[i];
      if (!panel) {
        this.panels[i] = null;
        tab.setAttribute("aria-disabled", "true");
        return;
      }

      tab.id = `${this.#tabBlockKey}-tab`;
      panel.id = `${this.#tabBlockKey}-panel`;
      panel.setAttribute("aria-labelledby", tab.id);

      if (this.isTopDownMode) {
        tab.href = `#${panel.id}`;
        panel.hidden = false;
      } else {
        tab.setAttribute("aria-controls", `${this.#tabBlockKey}-panel`);
        panel.setAttribute("role", "tabpanel"); // 탭 패널에 role="tabpanel" 속성 적용
        panel.hidden = true;
      }

      this.panels[i] = panel;
    });
  }

  #syncNoMatchPanelTabs() {
    this.tabs.forEach((tab) => {
      let panelId = null;

      if (this.isTopDownMode) {
        const anchorHref = tab.getAttribute("href");
        panelId =
          anchorHref?.indexOf("#") === 0 ? anchorHref?.split("#")[1] : null;
      } else {
        panelId = tab.getAttribute("aria-controls");
      }

      const hasPanel = this.panels.some((panel) => panel?.id === panelId);
      if (!hasPanel) {
        tab.setAttribute("aria-disabled", "true");
      }
    });
  }
}

if (!customElements.get("ui-tablist-group")) {
  customElements.define("ui-tablist-group", UiTablistGroup);
}

/**
 * <ui-slideshow>
 */
class UiSlideshow extends SwiperComponent {
  #controller = null;

  #selector = {
    pause: ".swiper-button-pause",
    play: ".swiper-button-play",
  };

  get slidesPerView() {
    const value = this.dataset.slidesPerView;
    return value ? Number(value) : 1;
  }

  get tabletSlidesPerView() {
    const value = this.dataset.tabletSlidesPerView;
    return value ? Number(value) : this.slidesPerView;
  }

  get mobileSlidesPerView() {
    const value = this.dataset.mobileSlidesPerView;
    return value ? Number(value) : this.slidesPerView;
  }

  get mobileWideSlidesPerView() {
    const value = this.dataset.mobileWideSlidesPerView;
    return value ? Number(value) : this.mobileSlidesPerView;
  }

  get slidesPerGroup() {
    const value = this.dataset.slidesPerGroup;
    return value ? Number(value) : 1;
  }

  get tabletSlidesPerGroup() {
    const value = this.dataset.tabletSlidesPerGroup;
    return value ? Number(value) : this.slidesPerGroup;
  }

  get mobileSlidesPerGroup() {
    const value = this.dataset.mobileSlidesPerGroup;
    return value ? Number(value) : this.slidesPerGroup;
  }

  get mobileWideSlidesPerGroup() {
    const value = this.dataset.mobileWideSlidesPerGroup;
    return value ? Number(value) : this.mobileSlidesPerGroup;
  }

  get spaceBetween() {
    return Number(this.dataset.spaceBetween) || 0;
  }

  get tabletSpaceBetween() {
    const value = this.dataset.tabletSpaceBetween;
    return value != null ? Number(value) : this.spaceBetween;
  }

  get mobileSpaceBetween() {
    const value = this.dataset.mobileSpaceBetween;
    return value != null ? Number(value) : this.spaceBetween;
  }

  get mobileWideSpaceBetween() {
    const value = this.dataset.mobileWideSpaceBetween;
    return value != null ? Number(value) : this.mobileSpaceBetween;
  }

  get loop() {
    const value = this.dataset.loop;
    return value === "false" ? false : true;
  }

  get autoplay() {
    const value = this.dataset.autoplay;
    return value === "true" ? true : false;
  }

  get autoplayDelay() {
    const value = this.dataset.autoplayDelay;
    return value ? Number(value) : 5000;
  }

  get effect() {
    return this.dataset.effect === "fade" ? "fade" : "slide";
  }

  get gridRows() {
    const value = this.dataset.gridRows;
    return value ? Number(value) : 0;
  }

  get tabletGridRows() {
    const value = this.dataset.tabletGridRows;
    return value != null && value !== "" ? Number(value) : this.gridRows;
  }

  get mobileGridRows() {
    const value = this.dataset.mobileGridRows;
    return value != null && value !== "" ? Number(value) : 0;
  }

  #gridOptions(rows) {
    return rows > 1 ? { grid: { rows, fill: "row" } } : {};
  }

  connectedCallback() {
    super.connectedCallback();
    this.#bindAutoplayToggle();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    super.disconnectedCallback();
  }

  setOptions() {
    return {
      ...super.setOptions(),
      watchSlidesProgress: true,
      slidesPerView: this.mobileSlidesPerView,
      slidesPerGroup: this.mobileSlidesPerGroup,
      spaceBetween: this.mobileSpaceBetween,
      loop: this.loop,
      autoplay: this.autoplay
        ? {
            delay: this.autoplayDelay,
            disableOnInteraction: false,
          }
        : false,
      effect: this.effect,
      ...(this.effect === "fade" && {
        fadeEffect: {
          crossFade: true,
        },
      }),
      ...this.#gridOptions(this.mobileGridRows),
      speed: 500,
      longSwipesRatio: 0.5,
      followFinger: this.effect === "fade" ? false : true,
      breakpoints: {
        540: {
          slidesPerView: this.mobileWideSlidesPerView,
          slidesPerGroup: this.mobileWideSlidesPerGroup,
          spaceBetween: this.mobileWideSpaceBetween,
          ...this.#gridOptions(this.mobileGridRows),
        },
        769: {
          slidesPerView: this.tabletSlidesPerView,
          slidesPerGroup: this.tabletSlidesPerGroup,
          spaceBetween: this.tabletSpaceBetween,
          ...this.#gridOptions(this.tabletGridRows),
        },
        1440: {
          slidesPerView: this.slidesPerView,
          slidesPerGroup: this.slidesPerGroup,
          spaceBetween: this.spaceBetween,
          ...this.#gridOptions(this.gridRows),
        },
      },
      on: {
        init: (swiper) => {
          this.#changeControlsColor(swiper);
          this.#updateMediaVisibility(swiper);
        },
        slideChange: (swiper) => {
          this.#changeControlsColor(swiper);
          this.#updateMediaVisibility(swiper);
        },
        loopFix: (swiper) => {
          this.#updateMediaVisibility(swiper);
        },
      },
    };
  }

  // 슬라이드의 동영상 일시정지/재개
  async #updateMediaVisibility(swiper) {
    const visibleSlides = new Set(swiper.visibleSlides);
    const items = [];

    for (const slide of swiper.slides) {
      const isVisible = visibleSlides.has(slide);
      for (const media of slide.querySelectorAll("deferred-media")) {
        items.push({ media, isVisible });
      }
    }

    if (!items.length) return;

    // handleSlideVisible/Hidden 호출 전에 deferred-media 클래스 정의가 끝날때까지 대기
    if (!customElements.get("deferred-media")) {
      await customElements.whenDefined("deferred-media");
    }

    for (const { media, isVisible } of items) {
      if (typeof media.handleSlideVisible !== "function") {
        customElements.upgrade(media);
      }

      // 보이는 슬라이드는 재개, 숨겨진 슬라이드는 일시정지
      if (isVisible) {
        media.handleSlideVisible?.();
      } else {
        media.handleSlideHidden?.();
      }
    }
  }

  replaceSlides(slides) {
    this.swiper.removeAllSlides();
    this.swiper.appendSlide(slides);
    this.swiper.update();
    this.swiper.slideTo(0);
  }

  #bindAutoplayToggle() {
    const pauseEl = this.querySelector(this.#selector.pause);
    const playEl = this.querySelector(this.#selector.play);
    if (!pauseEl || !playEl) return;

    this.#controller = new AbortController();
    const { signal } = this.#controller;

    pauseEl.addEventListener(
      "click",
      () => this.#pauseAutoplay(pauseEl, playEl),
      { signal },
    );
    playEl.addEventListener(
      "click",
      () => this.#startAutoplay(pauseEl, playEl),
      { signal },
    );
  }

  #pauseAutoplay(pauseEl, playEl) {
    this.swiper.autoplay.stop();
    pauseEl.hidden = true;
    playEl.hidden = false;
  }

  #startAutoplay(pauseEl, playEl) {
    this.swiper.autoplay.start();
    playEl.hidden = true;
    pauseEl.hidden = false;
  }

  #changeControlsColor(swiper) {
    if (!this.closest(".hero")) return;

    const swiperEl = swiper.el;
    const activeSlide = swiper.slides[swiper.activeIndex];

    const colorSchemePrefix = "color-scheme-";
    const colorSchemeEl = activeSlide?.querySelector(
      `[class*="${colorSchemePrefix}"]`,
    );
    if (!colorSchemeEl) return;

    const colorSchemeClass = [...colorSchemeEl.classList].find((c) =>
      c.startsWith(colorSchemePrefix),
    );
    const prev = [...swiperEl.classList].find((c) =>
      c.startsWith(colorSchemePrefix),
    );

    prev
      ? swiperEl.classList.replace(prev, colorSchemeClass)
      : swiperEl.classList.add(colorSchemeClass);
  }
}

if (!customElements.get("ui-slideshow")) {
  customElements.define("ui-slideshow", UiSlideshow);
}

/**
 * <ui-horizontal-scroll>
 */
class UiHorizontalScroll extends SwiperComponent {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener("click", this.#onSlideClick);
    this.addEventListener("change", this.#onCheckboxFilterChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener("click", this.#onSlideClick);
    this.removeEventListener("change", this.#onCheckboxFilterChange);
  }

  setOptions() {
    return {
      ...super.setOptions(),
      slidesPerView: "auto",
      freeMode: true,
    };
  }

  #onCheckboxFilterChange = (e) => {
    if (!e.target.matches(".checkbox-filter__field")) return;

    requestAnimationFrame(() => {
      const swiper = this.swiper;
      if (!swiper) return;

      const prevTranslate = swiper.translate;
      swiper.update();

      if (prevTranslate < swiper.maxTranslate()) {
        swiper.setTranslate(swiper.maxTranslate());
      }
    });
  };

  #onSlideClick = (e) => {
    const slide = e.target.closest(".swiper-slide");
    if (!slide) return;
    const slides = [...this.querySelectorAll(".swiper-slide")];
    const index = slides.indexOf(slide);
    if (index === -1) return;
    this.#scrollToActive(index);
  };

  #getPrevButtonOffset() {
    const prevEl = this.prevEl;
    if (!prevEl) return 0;
    if (prevEl.offsetWidth > 0) return prevEl.offsetWidth;
    const width = parseFloat(getComputedStyle(prevEl).width);
    return Number.isFinite(width) ? width : 0;
  }

  #scrollToActive(index) {
    const isTAB_PC = window.innerWidth >= 769;
    const willPrevBeVisible = isTAB_PC && index > 0 && !this.swiper?.isLocked;

    if (willPrevBeVisible && this.swiper?.slidesGrid?.length) {
      const slidePos = this.swiper.slidesGrid[index] ?? 0;
      const offset = this.#getPrevButtonOffset();
      const target = -(slidePos - offset);
      const clamped = Math.min(0, Math.max(this.swiper.maxTranslate(), target));
      this.swiper.translateTo(clamped, 300);
    } else {
      this.swiper?.slideTo(index);
    }
  }
}

if (!customElements.get("ui-horizontal-scroll")) {
  customElements.define("ui-horizontal-scroll", UiHorizontalScroll);
}

/**
 * <ui-accordion>
 */
class UiAccordion extends HTMLElement {
  #controller = new AbortController();

  get #isSingle() {
    return this.dataset.mode === "single";
  }

  #details() {
    return [...this.querySelectorAll(":scope > .details")].filter(
      (el) => el instanceof HTMLDetailsElement,
    );
  }

  connectedCallback() {
    const { signal } = this.#controller;
    const sync = () => {
      this.#setDefaultOpenStates();
      if (this.#isSingle) this.#ensureOneOpen();
    };

    sync();

    this.#details().forEach((details) => {
      const summary = details.querySelector("summary");
      summary?.addEventListener(
        "click",
        (event) => this.#onClick(event, details),
        { signal },
      );
      details.addEventListener("toggle", () => this.#onToggle(details), {
        signal,
      });
      details.addEventListener(
        "keydown",
        (event) => this.#onKeyDown(event, details),
        { signal },
      );
    });
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  #onClick(event, details) {
    if (this.#isSingle && details.open) event.preventDefault();
  }

  #onToggle(details) {
    if (!this.#isSingle) return;

    if (!details.open) {
      this.#ensureOneOpen();
      return;
    }

    this.#details().forEach((item) => {
      if (item !== details) item.open = false;
    });
  }

  #onKeyDown(event, details) {
    if (
      event.key !== "Escape" ||
      details.dataset.closeWithEscape !== "true" ||
      this.#isSingle
    )
      return;

    event.preventDefault();
    details.open = false;
    details.querySelector("summary")?.focus();
  }

  #ensureOneOpen() {
    const items = this.#details();
    if (items[0] && !items.some((item) => item.open)) items[0].open = true;
  }

  #setDefaultOpenStates() {
    this.#details().forEach((details) => {
      details.open = details.hasAttribute("open");
    });
  }

  openRowFromTarget(target) {
    const details = this.#details().find(
      (el) => el === target || el.contains(target),
    );
    if (details) details.open = true;
  }

  static openRowFromEventTarget(target) {
    if (!(target instanceof Element)) return;
    const accordion = target.closest("ui-accordion");
    if (accordion instanceof UiAccordion) {
      accordion.openRowFromTarget(target);
    }
  }
}

if (!customElements.get("ui-accordion")) {
  customElements.define("ui-accordion", UiAccordion);
}

if (window.Shopify?.designMode) {
  document.addEventListener("shopify:block:select", (event) => {
    UiAccordion.openRowFromEventTarget(event.target);
  });
}

/** @type {HTMLElement[]} 열린 `<ui-modal>` 스택 (마지막 = 가장 최근에 연 모달) */
const openModalStack = [];

/**
 * @param {HTMLElement} modal
 */
const pushOpenModal = (modal) => {
  const index = openModalStack.indexOf(modal);
  if (index !== -1) openModalStack.splice(index, 1);
  openModalStack.push(modal);
};

/**
 * @param {HTMLElement} modal
 */
const removeOpenModal = (modal) => {
  const index = openModalStack.indexOf(modal);
  if (index !== -1) openModalStack.splice(index, 1);
};

/**
 * @param {HTMLElement} modal
 * @returns {number}
 */
const getModalZIndex = (modal) => {
  const computed = Number.parseInt(getComputedStyle(modal).zIndex, 10);
  if (Number.isFinite(computed)) return computed;

  const layer = Number.parseInt(
    getComputedStyle(document.documentElement).getPropertyValue("--layer-dialog"),
    10,
  );
  return Number.isFinite(layer) ? layer : 99;
};

/**
 * @param {HTMLElement} modal
 */
const applyStackedModalZIndex = (modal) => {
  const previous = openModalStack[openModalStack.length - 2];
  if (!previous) {
    modal.style.removeProperty("z-index");
    return;
  }

  modal.style.zIndex = String(getModalZIndex(previous) + 1);
};

/**
 * @param {HTMLElement} modal
 */
const resetModalZIndex = (modal) => {
  modal.style.removeProperty("z-index");
};

/**
 * <ui-modal>
 */
class UiModal extends DialogComponent {
  #controller = null;
  #focusTrapController = null;
  #videoPlayTimeout = null;

  connectedCallback() {
    super.connectedCallback();

    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.addEventListener("dialog:open", this.#onDialogOpen, { signal });
    this.addEventListener("dialog:close", this.#onDialogClose, { signal });
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#clearVideoPlayTimeout();
    this.#abortFocusTrap();
    resetModalZIndex(this);
    removeOpenModal(this);
    super.disconnectedCallback();
  }

  #onDialogOpen = () => {
    const { dialog } = this.refs;
    if (!dialog) return;

    pushOpenModal(this);
    applyStackedModalZIndex(this);

    this.#abortFocusTrap();
    this.#focusTrapController = new AbortController();
    dialog.addEventListener("keydown", this.#handleFocusTrapKeydown, {
      signal: this.#focusTrapController.signal,
    });

    const focusable = getFocusableElements(dialog);
    (focusable[0] ?? dialog).focus();
  };

  #onDialogClose = () => {
    resetModalZIndex(this);
    removeOpenModal(this);
    this.#abortFocusTrap();
    this.#clearVideoMedia();
    this.#clearAlertMessage();
  };

  /**
   * Opens the video modal.
   * - Event trigger: `data-video-external-url` or `data-video-url`
   * - Options object: `{ videoExternalUrl | videoUrl, videoLabel?, videoPreviewImage?, videoAspectRatio? }`
   */
  openVideo(source) {
    const { dialog } = this.refs;
    if (!dialog || dialog.dataset.modalType !== "video") return;

    const options = this.#getOpenOptions(source);
    const mediaContainer = dialog.querySelector(".modal__media");
    if (!mediaContainer) return;

    const label = options.videoLabel || "Video";
    const externalUrl = options.videoExternalUrl;
    const videoUrl = options.videoUrl;

    this.#clearVideoMedia();

    if (externalUrl) {
      const iframe = this.#createVideoIframe(externalUrl, label);
      mediaContainer.appendChild(iframe);
      this.#scheduleVideoPlay(() => this.#playIframe(iframe));
    } else if (videoUrl) {
      const video = this.#createVideoElement({
        url: videoUrl,
        label,
        poster: options.videoPreviewImage,
        aspectRatio: options.videoAspectRatio,
      });
      mediaContainer.appendChild(video);
      this.#scheduleVideoPlay(() => {
        if (video.isConnected) video.play().catch(() => {});
      });
    } else {
      return;
    }

    this.showDialog();
  }

  /**
   * Opens the alert modal with a message inserted into `.modal__content > p`.
   * @param {string | Event | { message?: string, alertMessage?: string }} source
   */
  openAlert(source) {
    const { dialog } = this.refs;
    if (!dialog || dialog.dataset.modalType !== "alert") return;

    let message = "";
    if (typeof source === "string") {
      message = source;
    } else {
      const options = this.#getOpenOptions(source);
      message = options.message ?? options.alertMessage ?? "";
    }

    const messageEl = dialog.querySelector(".modal__content > p");
    if (!messageEl) return;

    messageEl.textContent = message;
    this.showDialog();
  }

  /**
   * @param {Event | Record<string, string> | null | undefined} source
   * @returns {Record<string, string>}
   */
  #getOpenOptions(source) {
    if (!source || typeof source === "string") return {};

    if (source instanceof Event || source.target instanceof Element) {
      const target = source.target;
      if (!(target instanceof Element)) return {};

      const trigger =
        target.closest(
          "[data-video-external-url], [data-video-url], [data-alert-message]",
        ) ?? target;

      if (!(trigger instanceof HTMLElement)) return {};
      return { ...trigger.dataset };
    }

    if (typeof source === "object")
      return /** @type {Record<string, string>} */ (source);

    return {};
  }

  #createVideoIframe(url, label) {
    const iframe = document.createElement("iframe");
    iframe.allow =
      "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture";
    iframe.allowFullscreen = true;
    iframe.title = label;

    const isVimeo = url.includes("vimeo.com");
    try {
      const embedUrl = new URL(url, window.location.origin);
      if (isVimeo) {
        embedUrl.searchParams.set("api", "1");
        embedUrl.searchParams.set("autoplay", "0");
        embedUrl.searchParams.set("muted", "1");
      } else {
        embedUrl.searchParams.set("enablejsapi", "1");
        embedUrl.searchParams.set("autoplay", "0");
        embedUrl.searchParams.set("mute", "1");
        embedUrl.searchParams.set("origin", window.location.origin);
      }
      iframe.src = embedUrl.toString();
    } catch {
      iframe.src = isVimeo
        ? `${url}?api=1&autoplay=0&muted=1`
        : `${url}?enablejsapi=1&autoplay=0&mute=1`;
    }

    return iframe;
  }

  #createVideoElement({ url, label = "Video", poster, aspectRatio }) {
    const video = document.createElement("video");
    video.autoplay = false;
    video.muted = true;
    video.loop = true;
    video.controls = true;
    video.playsInline = true;
    video.preload = "auto";
    video.setAttribute("aria-label", label);
    if (aspectRatio) video.style.setProperty("--aspect-ratio", aspectRatio);
    if (poster) video.poster = poster;
    video.src = url;
    return video;
  }

  #scheduleVideoPlay(play) {
    this.#clearVideoPlayTimeout();
    this.#videoPlayTimeout = setTimeout(play, 2000);
  }

  #clearVideoPlayTimeout() {
    if (this.#videoPlayTimeout == null) return;
    clearTimeout(this.#videoPlayTimeout);
    this.#videoPlayTimeout = null;
  }

  #playIframe(iframe) {
    if (!iframe.isConnected) return;

    const isVimeo = iframe.src.includes("vimeo.com");
    const message = isVimeo
      ? { method: "play" }
      : { event: "command", func: "playVideo", args: [] };

    iframe.contentWindow?.postMessage(JSON.stringify(message), "*");
  }

  #clearVideoMedia() {
    this.#clearVideoPlayTimeout();
    const { dialog } = this.refs;
    if (!dialog || dialog.dataset.modalType !== "video") return;

    const mediaContainer = dialog.querySelector(".modal__media");
    if (!mediaContainer) return;

    const video = mediaContainer.querySelector("video");
    if (video instanceof HTMLVideoElement) {
      video.pause();
    }

    mediaContainer.replaceChildren();
  }

  #clearAlertMessage() {
    const { dialog } = this.refs;
    if (!dialog || dialog.dataset.modalType !== "alert") return;

    const messageEl = dialog.querySelector(".modal__content > p");
    if (messageEl) messageEl.textContent = "";
  }

  #abortFocusTrap() {
    this.#focusTrapController?.abort();
    this.#focusTrapController = null;
  }

  #handleFocusTrapKeydown = (event) => {
    if (event.key !== "Tab") return;

    const { dialog } = this.refs;
    const focusable = getFocusableElements(dialog);

    if (!focusable.length) {
      event.preventDefault();
      dialog.focus();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;

    if (event.shiftKey) {
      if (active === first || !dialog.contains(active)) {
        event.preventDefault();
        last.focus();
      }
    } else if (active === last || !dialog.contains(active)) {
      event.preventDefault();
      first.focus();
    }
  };
}

if (!customElements.get("ui-modal")) {
  customElements.define("ui-modal", UiModal);
}

/**
 * <ui-show-more>
 * 화면 크기별로 일부 항목만 보여주고, 토글 버튼을 누르면 나머지 항목이 모두 보이게 함
 * 더보기 버튼은 보이는 개수보다 항목이 많을 때만 노출됨 (모바일 5개 / PC&TAB 12개 기준)
 */
class UiShowMore extends HTMLElement {
  #controller = null;
  #mobileMediaQuery = window.matchMedia("(max-width: 768px)");

  #selector = {
    toggle: ".button--toggle",
    item: ".card-item",
  };

  static #VISIBLE_MOBILE = 5;
  static #VISIBLE_PC = 12;

  get isExpanded() {
    return this.toggle?.getAttribute("aria-expanded") === "true";
  }

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.toggle = this.querySelector(this.#selector.toggle);
    if (!this.toggle) return;

    this.#syncVisibility();

    this.#mobileMediaQuery.addEventListener("change", this.#syncVisibility, {
      signal,
    });
    this.toggle.addEventListener("click", this.#onClick, { signal });
    this.addEventListener("show-more:update", this.#syncVisibility, { signal });
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  #getVisibleLimit() {
    return this.#mobileMediaQuery.matches
      ? UiShowMore.#VISIBLE_MOBILE
      : UiShowMore.#VISIBLE_PC;
  }

  #syncVisibility = () => {
    const allItems = this.querySelectorAll(this.#selector.item);
    const visibleItems = Array.from(allItems).filter(item => !item.classList.contains('js-search-hidden'));
    const limit = this.#getVisibleLimit();
    const needsToggle = visibleItems.length > limit;

    if (!needsToggle) this.#resetToggle();

    const expanded = this.isExpanded;
    visibleItems.forEach((item, index) => {
      item.hidden = !expanded && index >= limit;
    });

    allItems.forEach(item => {
      if (item.classList.contains('js-search-hidden')) {
        item.hidden = false;
      }
    });

    this.toggle.hidden = !needsToggle;
  };

  #resetToggle() {
    if (!this.isExpanded) return;

    this.toggle.setAttribute("aria-expanded", "false");
    const text = this.toggle.querySelector(".button__text");
    if (text) text.textContent = this.toggle.dataset.moreText;
  }

  #onClick = () => {
    const expanded = !this.isExpanded;

    this.toggle.setAttribute("aria-expanded", String(expanded));
    const text = this.toggle.querySelector(".button__text");

    if (text) {
      text.textContent = expanded
        ? this.toggle.dataset.lessText
        : this.toggle.dataset.moreText;
    }

    this.#syncVisibility();
  };
}

if (!customElements.get("ui-show-more")) {
  customElements.define("ui-show-more", UiShowMore);
}

const TOAST_VARIANTS = new Set([
  "success",
  "error",
  "warning",
  "info",
  "incorrect",
]);

/** `assets/icon-close.svg` — `currentColor`로 variant별 닫기 아이콘 색상 적용 */
const TOAST_CLOSE_ICON_SVG =
  '<svg aria-hidden="true" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">' +
  '<path d="M13.1221 2.15063C13.323 1.94979 13.6485 1.94979 13.8494 2.15063C14.0502 2.35147 14.0502 2.67703 13.8494 2.87787L8.72724 8L13.8494 13.1221C14.0502 13.323 14.0502 13.6485 13.8494 13.8494C13.6485 14.0502 13.323 14.0502 13.1221 13.8494L8 8.72724L2.87787 13.8494C2.67703 14.0502 2.35147 14.0502 2.15063 13.8494C1.94979 13.6485 1.94979 13.323 2.15063 13.1221L7.27276 8L2.15063 2.87787C1.94979 2.67703 1.94979 2.35147 2.15063 2.15063C2.35147 1.94979 2.67703 1.94979 2.87787 2.15063L8 7.27276L13.1221 2.15063Z" fill="currentColor"/>' +
  "</svg>";

/** success 체크 — Figma 스펙 white 아이콘 (viewBox crop, `currentColor`) */
const TOAST_SUCCESS_ICON_SVG =
  '<svg aria-hidden="true" viewBox="292 234 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">' +
  '<path d="M306.26 239.186C306.631 238.777 307.264 238.747 307.673 239.118C308.081 239.49 308.112 240.123 307.74 240.531L300.281 248.736C300.165 248.863 300.023 248.965 299.865 249.035C299.707 249.105 299.536 249.142 299.363 249.142C299.191 249.142 299.019 249.105 298.861 249.035C298.703 248.965 298.561 248.863 298.445 248.736L294.26 244.131C293.889 243.722 293.919 243.09 294.327 242.719C294.736 242.347 295.369 242.378 295.74 242.786L299.362 246.771L306.26 239.186Z" fill="currentColor"/>' +
  "</svg>";

/** @type {number | null} */
let activeToastTimer = null;

/** @param {HTMLElement | null} toastEl */
const dismissToast = (toastEl) => {
  if (!toastEl) return;

  if (activeToastTimer !== null) {
    clearTimeout(activeToastTimer);
    activeToastTimer = null;
  }

  toastEl.remove();
};

/**
 * 화면 하단 중앙 토스트 (`.toast-popup.full`, 스타일은 theme/_toast.scss)
 * @param {string} message
 * @param {object} [options]
 * @param {'success' | 'error' | 'warning' | 'info' | 'incorrect'} [options.variant='success']
 * @param {number} [options.duration=3000] 자동 닫힘(ms). 0이면 자동 닫힘 없음
 * @param {string} [options.closeLabel='Close'] 닫기 버튼 스크린리더 텍스트
 * @param {boolean} [options.dismissible=true] 닫기 버튼 표시 여부
 * @returns {HTMLDivElement}
 */
const showToast = (message, options = {}) => {
  const {
    variant = "success",
    duration = 3000,
    closeLabel = "Close",
    dismissible = true,
  } = options;

  const safeVariant = TOAST_VARIANTS.has(variant) ? variant : "success";

  document.querySelector(".toast-popup.full")?.remove();
  if (activeToastTimer !== null) {
    clearTimeout(activeToastTimer);
    activeToastTimer = null;
  }

  const toastEl = document.createElement("div");
  toastEl.className = "toast-popup full";
  toastEl.setAttribute("role", "alert");
  toastEl.setAttribute("aria-live", "assertive");
  toastEl.setAttribute("aria-atomic", "true");

  const item = document.createElement("div");
  item.className = `toast-popup__item toast-popup__item--${safeVariant}`;

  const container = document.createElement("div");
  container.className = "toast-popup__container";

  const icon = document.createElement("i");
  icon.className = `toast-popup__icon toast-popup__icon--${safeVariant}`;
  if (safeVariant === "success") {
    icon.insertAdjacentHTML("afterbegin", TOAST_SUCCESS_ICON_SVG);
  }
  const iconLabel = document.createElement("span");
  iconLabel.className = "visually-hidden";
  iconLabel.setAttribute("aria-hidden", "true");
  iconLabel.textContent = safeVariant;
  icon.appendChild(iconLabel);

  const title = document.createElement("div");
  title.className = "toast-popup__title typo typo--body3";
  title.textContent = message;

  container.append(icon, title);
  item.appendChild(container);

  if (dismissible) {
    const removeBtn = document.createElement("button");
    removeBtn.className = "toast-popup__remove";
    removeBtn.type = "button";
    removeBtn.insertAdjacentHTML("beforeend", TOAST_CLOSE_ICON_SVG);
    const removeLabel = document.createElement("span");
    removeLabel.className = "visually-hidden";
    removeLabel.textContent = closeLabel;
    removeBtn.appendChild(removeLabel);
    removeBtn.addEventListener("click", () => {
      if (toastEl.id === "sign-help") return;
      dismissToast(toastEl);
    });
    item.appendChild(removeBtn);
  }

  toastEl.appendChild(item);
  document.body.appendChild(toastEl);

  if (duration > 0) {
    activeToastTimer = window.setTimeout(() => dismissToast(toastEl), duration);
  }

  return toastEl;
};

/** inline script · PDP/PLP 등에서 호출 (type="module"은 전역 스코프 분리) */
window.showToast = showToast;

/**
 * Model name 복사 — PDP / PLP / Compare / 가이드 공통
 * - 복사 텍스트는 버튼의 `data-modelname`만 사용
 * - PDP: `.sku-copy`에 `.btn-copy[data-modelname]` 동적 주입
 * - PLP·Compare: `.copy-button button[data-modelname]` 클릭
 * - 이벤트 위임 + MutationObserver로 동적 DOM에도 동작
 */
const MODELNAME_COPY_DEFAULTS = {
  copyBtnAlt: "Copy model name",
  closBtnAlt: "Close",
  successMsg: "The model name has been copied",
};

const getModelNameCopyConfig = () => {
  const el = document.querySelector(".productGlobal__config");
  if (!(el instanceof HTMLElement)) return { ...MODELNAME_COPY_DEFAULTS };

  const raw = el.dataset.copyModelname;
  if (!raw) return { ...MODELNAME_COPY_DEFAULTS };

  try {
    return { ...MODELNAME_COPY_DEFAULTS, ...JSON.parse(raw) };
  } catch {
    return { ...MODELNAME_COPY_DEFAULTS };
  }
};

const copyTextToClipboard = (text) => {
  if (navigator.clipboard?.writeText) {
    return navigator.clipboard.writeText(text);
  }

  const tempInput = document.createElement("input");
  tempInput.value = text;
  document.body.appendChild(tempInput);
  tempInput.select();
  document.execCommand("copy");
  document.body.removeChild(tempInput);
  return Promise.resolve();
};

/** PDP `.sku-copy` 영역에 `.btn-copy`가 없으면 생성 */
const ensurePdpModelNameCopyButtons = (root = document) => {
  if (
    !(
      root instanceof Element ||
      root instanceof Document ||
      root instanceof DocumentFragment
    )
  ) {
    return;
  }

  const { copyBtnAlt } = getModelNameCopyConfig();
  const skuCopyNodes = [];

  if (root instanceof Element && root.matches(".sku-copy")) {
    skuCopyNodes.push(root);
  }
  root.querySelectorAll(".sku-copy").forEach((el) => skuCopyNodes.push(el));

  skuCopyNodes.forEach((skuCopy) => {
    if (!(skuCopy instanceof HTMLElement)) return;
    if (skuCopy.querySelector(".btn-copy")) return;

    const area =
      skuCopy.closest(".c-text-contents__sku, .c-text-contents__eyebrow") ??
      skuCopy.parentElement;
    if (!(area instanceof HTMLElement)) return;
    if (area.closest("a")) return;
    if (area.querySelector(".btn-copy")) return;

    const span = area.querySelector(
      ".cmp-text span, span.cmp-text, span.typo, span",
    );
    const modelName = span?.textContent?.trim() ?? "";
    if (!modelName) return;

    const buttonEl = document.createElement("button");
    buttonEl.className = "btn-copy button button--icon-only";
    buttonEl.type = "button";
    buttonEl.setAttribute("data-modelname", modelName);

    const COPY_ICON_SVG =
      '<svg aria-hidden="true" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">' +
      '<path d="M10.5862 5.85312C10.586 5.61049 10.3895 5.414 10.1469 5.41384H3.46853C3.22576 5.41384 3.02873 5.61039 3.02857 5.85312V12.5315C3.02857 12.7743 3.22566 12.9714 3.46853 12.9714H10.1469C10.3896 12.9713 10.5862 12.7742 10.5862 12.5315V5.85312ZM12.9714 10.1469V3.46853C12.9714 3.22566 12.7743 3.02857 12.5315 3.02857H5.85312C5.56923 3.02841 5.33884 2.79822 5.33884 2.51429C5.33884 2.23035 5.56923 2.00016 5.85312 2H12.5315C13.3424 2 14 2.65759 14 3.46853V10.1469C13.9998 10.4308 13.7696 10.6612 13.4857 10.6612C13.2018 10.6612 12.9716 10.4308 12.9714 10.1469ZM11.6147 12.5315C11.6147 13.3423 10.9577 13.9998 10.1469 14H3.46853C2.65759 14 2 13.3424 2 12.5315V5.85312C2.00016 5.04233 2.65769 4.38527 3.46853 4.38527H10.1469C10.9576 4.38543 11.6146 5.04242 11.6147 5.85312V12.5315Z" fill="currentColor"/>' +
      "</svg>";
    const labelEl = document.createElement("span");
    labelEl.className = "sr-only";
    labelEl.textContent = copyBtnAlt;
    const iconEl = document.createElement("span");
    iconEl.className = "icon size-16";
    iconEl.insertAdjacentHTML("afterbegin", COPY_ICON_SVG);
    buttonEl.appendChild(iconEl);
    buttonEl.appendChild(labelEl);

    skuCopy.appendChild(buttonEl);
  });
};

const initModelNameCopy = () => {
  if (document.documentElement.dataset.modelNameCopyInit === "1") {
    ensurePdpModelNameCopyButtons(document);
    return;
  }
  document.documentElement.dataset.modelNameCopyInit = "1";

  ensurePdpModelNameCopyButtons(document);

  document.addEventListener("click", (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const btn = target.closest(".btn-copy, .copy-button button");
    if (!(btn instanceof HTMLElement)) return;

    const modelName = btn.getAttribute("data-modelname")?.trim();
    if (!modelName) return;

    event.preventDefault();
    event.stopPropagation();

    const { successMsg, closBtnAlt } = getModelNameCopyConfig();
    void copyTextToClipboard(modelName).then(() => {
      showToast(successMsg, { closeLabel: closBtnAlt });
    });
  });

  const observer = new MutationObserver((mutations) => {
    let shouldEnsure = false;
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (!(node instanceof Element)) continue;
        if (
          node.matches(
            ".sku-copy, .c-text-contents__sku, .c-text-contents__eyebrow",
          ) ||
          node.querySelector?.(".sku-copy")
        ) {
          shouldEnsure = true;
          break;
        }
      }
      if (shouldEnsure) break;
    }
    if (shouldEnsure) ensurePdpModelNameCopyButtons(document);
  });

  const startObserver = () => {
    if (!document.body) return;
    observer.observe(document.body, { childList: true, subtree: true });
  };

  if (document.body) {
    startObserver();
  } else {
    document.addEventListener("DOMContentLoaded", startObserver, {
      once: true,
    });
  }
};

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initModelNameCopy, {
    once: true,
  });
} else {
  initModelNameCopy();
}

window.initModelNameCopy = initModelNameCopy;
window.ensurePdpModelNameCopyButtons = ensurePdpModelNameCopyButtons;

/**
 * Opens a `<ui-modal>` by id.
 * Type-specific options are applied when provided; otherwise calls `showDialog()`.
 *
 * @param {string} modalId e.g. `'modal-alert'`, `'video-modal'`, `'modal-download'`
 * @param {string | {
 *   message?: string,
 *   alertMessage?: string,
 *   videoExternalUrl?: string,
 *   videoUrl?: string,
 *   videoLabel?: string,
 *   videoPreviewImage?: string,
 *   videoAspectRatio?: string,
 * }} [options] Alert: message string or `{ message }`. Video: URL fields.
 * @returns {HTMLElement | undefined}
 */
const openModal = (modalId, options = {}) => {
  const modal = document.getElementById(modalId);
  if (!modal) return;

  const dialog = modal.refs?.dialog ?? modal.querySelector("dialog");
  const type = dialog?.dataset?.modalType;
  const opts = typeof options === "string" ? { message: options } : options;

  if (type === "alert" && typeof modal.openAlert === "function") {
    modal.openAlert(opts.message ?? opts.alertMessage ?? "");
    return modal;
  }

  if (
    type === "video" &&
    typeof modal.openVideo === "function" &&
    (opts.videoExternalUrl || opts.videoUrl)
  ) {
    modal.openVideo(opts);
    return modal;
  }

  if (typeof modal.showDialog !== "function") return;

  modal.showDialog();
  return modal;
};

window.openModal = openModal;

/**
 * Opens the shared alert modal (`#modal-alert`) with the given message.
 * @param {string} message
 * @returns {HTMLElement | undefined}
 */
const showAlert = (message) => openModal("modal-alert", { message });

window.showAlert = showAlert;

/**
 * Closes a `<ui-modal>` by id, or the most recently opened one if omitted.
 * @param {string} [modalId] e.g. `'modal-alert'`, `'video-modal'`
 * @returns {Promise<HTMLElement | undefined>}
 */
const closeModal = async (modalId) => {
  const modal = modalId
    ? document.getElementById(modalId)
    : (openModalStack[openModalStack.length - 1] ??
      document.querySelector("ui-modal dialog[open]")?.closest("ui-modal"));

  if (!modal || typeof modal.closeDialog !== "function") return;

  await modal.closeDialog();
  return modal;
};

window.closeModal = closeModal;

/** Footer scroll-top: 스크롤 시 표시, 풋터 진입 시 class 토글 (클릭은 scroll-to-anchor가 처리) */
{
  const button = document.querySelector(".top__bottom");
  const footer = document.querySelector(".footer-section");
  const ABOVE_FOOTER_CLASS = "is-above-footer";
  const VISIBLE_CLASS = "is-visible";
  /** 이 값(px) 이상 스크롤했을 때만 탑 버튼 표시 */
  const SHOW_AFTER_SCROLL = 130;

  if (button && footer) {
    const update = () => {
      const scrollTop =
        window.scrollY || document.documentElement.scrollTop || 0;
      const isScrolled = scrollTop > SHOW_AFTER_SCROLL;
      const { top } = footer.getBoundingClientRect();
      const isAboveFooter = top < window.innerHeight;

      button.classList.toggle(VISIBLE_CLASS, isScrolled);
      button.classList.toggle(ABOVE_FOOTER_CLASS, isAboveFooter);
      button.setAttribute("aria-hidden", isScrolled ? "false" : "true");
    };

    const refresh = () => requestAnimationFrame(update);

    refresh();
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", refresh, { once: true });
    }
    window.addEventListener("load", refresh, { once: true });
    window.addEventListener("pageshow", refresh);

    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update, { passive: true });
    new ResizeObserver(update).observe(footer);
  }
}

/*
 * <anchored-tooltip-component>
 */
class AnchoredTooltipComponent extends Component {
  requiredRefs = ["trigger", "content"];
  #controller = new AbortController();
  #isOpen = false;

  connectedCallback() {
    super.connectedCallback();
    const { signal } = this.#controller;
    const { trigger, content } = this.refs;

    requestAnimationFrame(() => this.#updateTriggerCenter());

    this.#ensureAccessibility(trigger, content);
    this.#close();

    trigger.addEventListener("click", this.#onTriggerClick, { signal });
    document.addEventListener("click", this.#onDocumentClick, { signal });
    document.addEventListener("keydown", this.#onDocumentKeyDown, { signal });

    if (this.dataset.closeOnResize !== undefined) {
      window.addEventListener("resize", this.#onResize, { signal });
    }

    if (this.dataset.closeOnScroll !== undefined) {
      document.addEventListener("scroll", this.#onScroll, {
        signal,
        capture: true,
        passive: true,
      });
    }
  }

  #updateTriggerCenter() {
    const { trigger } = this.refs;
    const hostRect = this.getBoundingClientRect();
    const triggerRect = trigger.getBoundingClientRect();

    if (triggerRect.width === 0) return;

    const center = triggerRect.left - hostRect.left + triggerRect.width / 2;
    this.style.setProperty("--trigger-center", `${center}px`);
  }

  disconnectedCallback() {
    this.#controller.abort();
    super.disconnectedCallback();
  }

  #ensureAccessibility(trigger, content) {
    if (!content.id) content.id = `AnchoredTooltip-${crypto.randomUUID()}`;
    if (!trigger.id)
      trigger.id = `AnchoredTooltipTrigger-${crypto.randomUUID()}`;
    if (!trigger.getAttribute("aria-controls"))
      trigger.setAttribute("aria-controls", content.id);

    content.setAttribute("role", "region");
    content.setAttribute("aria-labelledby", trigger.id);
  }

  #open() {
    const { trigger, content } = this.refs;
    this.#updateTriggerCenter();
    this.#isOpen = true;
    content.hidden = false;
    trigger.setAttribute("aria-expanded", "true");
    this.classList.add("is-open");
  }

  #close() {
    const { trigger, content } = this.refs;
    this.#isOpen = false;
    content.hidden = true;
    trigger.setAttribute("aria-expanded", "false");
    this.classList.remove("is-open");
  }

  #onTriggerClick = (event) => {
    event.preventDefault();
    this.#isOpen ? this.#close() : this.#open();
  };

  #onDocumentClick = (event) => {
    if (!this.#isOpen) return;
    if (!this.contains(/** @type {Node} */ (event.target))) this.#close();
  };

  #onDocumentKeyDown = (event) => {
    if (!this.#isOpen || event.key !== "Escape") return;
    event.preventDefault();
    this.#close();
    this.refs.trigger.focus();
  };

  #onResize = () => {
    if (this.#isOpen) this.#close();
  };

  #onScroll = (event) => {
    if (!this.#isOpen) return;

    const scrollTarget = event.target;
    if (!(scrollTarget instanceof Element)) return;
    if (this.refs.content.contains(scrollTarget)) return;

    const scrollRoot =
      this.closest("dialog, .dialog-modal, .facets--drawer") ??
      this.closest("facets-form-component, .facets__form");

    if (!scrollRoot) return;
    if (scrollRoot === scrollTarget || scrollRoot.contains(scrollTarget)) {
      this.#close();
    }
  };
}

if (!customElements.get("anchored-tooltip-component")) {
  customElements.define("anchored-tooltip-component", AnchoredTooltipComponent);
}

/**
 * Heading / Image markup 디버그 오버레이
 * 콘솔·인라인에서 `debugMarkup()` 호출 — 재호출 또는 패널「끄기」로 해제
 * sessionStorage에 활성 상태를 저장해 페이지 이동 후에도 유지
 * @returns {boolean} 활성화 여부
 */
const DEBUG_MARKUP_STORAGE_KEY = "debugMarkup:enabled";
const DEBUG_MARKUP_HEADINGS_KEY = "debugMarkup:headings";
const DEBUG_MARKUP_IMAGES_KEY = "debugMarkup:images";
const DEBUG_MARKUP_STYLE_ID = "debug-markup-style";
const DEBUG_MARKUP_PANEL_ID = "debug-markup-panel";
const DEBUG_MARKUP_VEIL_ID = "debug-markup-veil";
const DEBUG_MARKUP_ATTR = "data-debug-markup";
const DEBUG_MARKUP_HIT_ATTR = "data-debug-markup-hit";
const DEBUG_MARKUP_HEADING_SELECTOR = "h1, h2, h3, h4, h5, h6";
const DEBUG_MARKUP_IMAGE_SELECTOR = "img";
const DEBUG_MARKUP_IMAGE_COLOR = "#00acc1";
const DEBUG_MARKUP_IMAGE_MISSING_COLOR = "#e53935";
const DEBUG_MARKUP_LEVEL_COLORS = {
  1: "#e53935",
  2: "#fb8c00",
  3: "#fdd835",
  4: "#43a047",
  5: "#1e88e5",
  6: "#8e24aa",
};

/** @type {{ el: Element, hit: HTMLElement }[]} */
let debugMarkupHits = [];
/** @type {(() => void) | null} */
let debugMarkupSyncCleanup = null;
/** @type {MutationObserver | null} */
let debugMarkupObserver = null;
/** @type {ReturnType<typeof setTimeout> | null} */
let debugMarkupRefreshTimer = null;
/** @type {ReturnType<typeof setTimeout> | null} */
let debugMarkupRefreshFollowUpTimer = null;
let debugMarkupApplying = false;
/** 깜빡임 중 refresh로 DOM이 교체되지 않도록 잠금 시각 */
let debugMarkupFlashUntil = 0;
const debugMarkupHitMap = new WeakMap();

const isDebugMarkupEnabled = () =>
  sessionStorage.getItem(DEBUG_MARKUP_STORAGE_KEY) === "1";

const setDebugMarkupEnabled = (enabled) => {
  if (enabled) {
    sessionStorage.setItem(DEBUG_MARKUP_STORAGE_KEY, "1");
    if (sessionStorage.getItem(DEBUG_MARKUP_HEADINGS_KEY) == null) {
      sessionStorage.setItem(DEBUG_MARKUP_HEADINGS_KEY, "1");
    }
    if (sessionStorage.getItem(DEBUG_MARKUP_IMAGES_KEY) == null) {
      sessionStorage.setItem(DEBUG_MARKUP_IMAGES_KEY, "1");
    }
  } else {
    sessionStorage.removeItem(DEBUG_MARKUP_STORAGE_KEY);
  }
};

const isDebugMarkupHeadingsEnabled = () =>
  sessionStorage.getItem(DEBUG_MARKUP_HEADINGS_KEY) !== "0";

const isDebugMarkupImagesEnabled = () =>
  sessionStorage.getItem(DEBUG_MARKUP_IMAGES_KEY) !== "0";

const setDebugMarkupHeadingsEnabled = (enabled) => {
  sessionStorage.setItem(DEBUG_MARKUP_HEADINGS_KEY, enabled ? "1" : "0");
};

const setDebugMarkupImagesEnabled = (enabled) => {
  sessionStorage.setItem(DEBUG_MARKUP_IMAGES_KEY, enabled ? "1" : "0");
};

const hexToRgba = (hex, alpha) => {
  const raw = hex.replace("#", "");
  const normalized =
    raw.length === 3
      ? raw
          .split("")
          .map((ch) => ch + ch)
          .join("")
      : raw;
  const value = Number.parseInt(normalized, 16);
  const r = (value >> 16) & 255;
  const g = (value >> 8) & 255;
  const b = value & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const isDebugMarkupOwnNode = (node) => {
  if (!(node instanceof Element)) {
    return node?.parentElement
      ? isDebugMarkupOwnNode(node.parentElement)
      : false;
  }

  if (
    node.id === DEBUG_MARKUP_STYLE_ID ||
    node.id === DEBUG_MARKUP_PANEL_ID ||
    node.id === DEBUG_MARKUP_VEIL_ID ||
    node.hasAttribute(DEBUG_MARKUP_HIT_ATTR)
  ) {
    return true;
  }

  return Boolean(
    node.closest(
      `#${DEBUG_MARKUP_PANEL_ID}, #${DEBUG_MARKUP_VEIL_ID}, [${DEBUG_MARKUP_HIT_ATTR}]`,
    ),
  );
};

const teardownDebugMarkupUI = () => {
  const panelScrollTop =
    document.getElementById(DEBUG_MARKUP_PANEL_ID)?.scrollTop ?? 0;

  document.getElementById(DEBUG_MARKUP_STYLE_ID)?.remove();
  document.getElementById(DEBUG_MARKUP_PANEL_ID)?.remove();
  document.getElementById(DEBUG_MARKUP_VEIL_ID)?.remove();
  document.querySelectorAll(`[${DEBUG_MARKUP_HIT_ATTR}]`).forEach((el) => {
    el.remove();
  });
  document.querySelectorAll(`[${DEBUG_MARKUP_ATTR}]`).forEach((el) => {
    el.removeAttribute(DEBUG_MARKUP_ATTR);
  });
  debugMarkupHits = [];

  return panelScrollTop;
};

const stopDebugMarkupWatchers = () => {
  if (debugMarkupRefreshTimer != null) {
    window.clearTimeout(debugMarkupRefreshTimer);
    debugMarkupRefreshTimer = null;
  }
  if (debugMarkupRefreshFollowUpTimer != null) {
    window.clearTimeout(debugMarkupRefreshFollowUpTimer);
    debugMarkupRefreshFollowUpTimer = null;
  }
  debugMarkupObserver?.disconnect();
  debugMarkupObserver = null;
  debugMarkupSyncCleanup?.();
  debugMarkupSyncCleanup = null;
};

const teardownDebugMarkup = () => {
  stopDebugMarkupWatchers();
  teardownDebugMarkupUI();
};

const disableDebugMarkup = () => {
  setDebugMarkupEnabled(false);
  teardownDebugMarkup();
  return false;
};

const scheduleDebugMarkupRefresh = () => {
  if (!isDebugMarkupEnabled() || debugMarkupApplying) return;
  if (Date.now() < debugMarkupFlashUntil) return;

  if (debugMarkupRefreshTimer != null) {
    window.clearTimeout(debugMarkupRefreshTimer);
  }
  debugMarkupRefreshTimer = window.setTimeout(() => {
    debugMarkupRefreshTimer = null;
    if (!isDebugMarkupEnabled() || debugMarkupApplying) return;
    if (Date.now() < debugMarkupFlashUntil) return;
    applyDebugMarkup();

    if (debugMarkupRefreshFollowUpTimer != null) {
      window.clearTimeout(debugMarkupRefreshFollowUpTimer);
    }
    // 탭/아코디언 transition 이후 재측정
    debugMarkupRefreshFollowUpTimer = window.setTimeout(() => {
      debugMarkupRefreshFollowUpTimer = null;
      if (!isDebugMarkupEnabled() || debugMarkupApplying) return;
      if (Date.now() < debugMarkupFlashUntil) return;
      applyDebugMarkup();
    }, 320);
  }, 80);
};

const startDebugMarkupWatchers = () => {
  if (debugMarkupSyncCleanup) return;

  const onScrollOrResize = () => syncDebugMarkupHits();
  const onInteractiveChange = (event) => {
    const target = /** @type {EventTarget | null} */ (event.target);
    if (target instanceof Node && isDebugMarkupOwnNode(target)) return;
    scheduleDebugMarkupRefresh();
  };

  window.addEventListener("scroll", onScrollOrResize, true);
  window.addEventListener("resize", onScrollOrResize);
  document.addEventListener("click", onInteractiveChange, true);
  document.addEventListener("change", onInteractiveChange, true);
  document.addEventListener("transitionend", onInteractiveChange, true);
  document.addEventListener("animationend", onInteractiveChange, true);
  document.addEventListener("shopify:section:load", onInteractiveChange);
  document.addEventListener("shopify:section:unload", onInteractiveChange);

  debugMarkupObserver = new MutationObserver((mutations) => {
    if (debugMarkupApplying || !isDebugMarkupEnabled()) return;

    const relevant = mutations.some((mutation) => {
      if (isDebugMarkupOwnNode(mutation.target)) return false;

      if (mutation.type === "childList") {
        const nodes = [
          ...mutation.addedNodes,
          ...mutation.removedNodes,
        ];
        return nodes.some((node) => !isDebugMarkupOwnNode(node));
      }

      return true;
    });

    if (relevant) scheduleDebugMarkupRefresh();
  });

  debugMarkupObserver.observe(document.documentElement, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: [
      "class",
      "style",
      "hidden",
      "aria-hidden",
      "aria-selected",
      "aria-expanded",
      "open",
      "alt",
      "src",
      "srcset",
    ],
  });

  debugMarkupSyncCleanup = () => {
    window.removeEventListener("scroll", onScrollOrResize, true);
    window.removeEventListener("resize", onScrollOrResize);
    document.removeEventListener("click", onInteractiveChange, true);
    document.removeEventListener("change", onInteractiveChange, true);
    document.removeEventListener("transitionend", onInteractiveChange, true);
    document.removeEventListener("animationend", onInteractiveChange, true);
    document.removeEventListener("shopify:section:load", onInteractiveChange);
    document.removeEventListener(
      "shopify:section:unload",
      onInteractiveChange,
    );
    debugMarkupObserver?.disconnect();
    debugMarkupObserver = null;
  };
};

/** display/visibility/opacity·크기 기준으로 화면에서 보이는지 판별 */
const isDebugMarkupVisible = (el) => {
  if (typeof el.checkVisibility === "function") {
    return el.checkVisibility({
      checkOpacity: true,
      checkVisibilityCSS: true,
      contentVisibilityAuto: true,
    });
  }

  const style = window.getComputedStyle(el);
  if (
    style.display === "none" ||
    style.visibility === "hidden" ||
    Number.parseFloat(style.opacity) === 0
  ) {
    return false;
  }

  const rect = el.getBoundingClientRect();
  return rect.width > 0 || rect.height > 0;
};

const getImageAltLabel = (img) => {
  if (!img.hasAttribute("alt")) return "(no alt)";
  const alt = img.getAttribute("alt");
  if (alt === "") return 'alt=""';
  return alt;
};

/** a/button 안에 이미지와 함께 의미 있는 텍스트가 있는지 */
const isImageInsideControlWithText = (img) => {
  const control = img.closest("a, button");
  if (!control) return false;

  const clone = /** @type {HTMLElement} */ (control.cloneNode(true));
  clone
    .querySelectorAll('img, svg, noscript, [aria-hidden="true"]')
    .forEach((el) => el.remove());
  return Boolean(clone.textContent?.replace(/\s+/g, " ").trim());
};

/**
 * alt 경고(빨간) 대상:
 * - alt 속성 없음 → 항상 빨강
 * - alt="" → a/button+텍스트와 함께면 파랑(OK), 그 외 빨강
 */
const isImageAltIssue = (img) => {
  if (!img.hasAttribute("alt")) return true;
  if (img.getAttribute("alt") === "") {
    return !isImageInsideControlWithText(img);
  }
  return false;
};

const truncateDebugLabel = (text, max = 80) => {
  const value = String(text);
  return value.length > max ? `${value.slice(0, max - 1)}…` : value;
};

const syncDebugMarkupHits = () => {
  debugMarkupHits.forEach(({ el, hit }) => {
    const rect = el.getBoundingClientRect();
    const visible = rect.width > 0 || rect.height > 0;
    hit.hidden = !visible;
    if (!visible) return;
    hit.style.top = `${rect.top}px`;
    hit.style.left = `${rect.left}px`;
    hit.style.width = `${rect.width}px`;
    hit.style.height = `${rect.height}px`;
  });
};

const createDebugMarkupHit = (el, label, color) => {
  const hit = document.createElement("div");
  hit.setAttribute(DEBUG_MARKUP_HIT_ATTR, "");
  hit.style.setProperty("--debug-markup-color", color);
  hit.style.setProperty("--debug-markup-fill", hexToRgba(color, 0.2));

  const badge = document.createElement("span");
  badge.className = "debug-markup-badge";
  badge.textContent = label;
  hit.appendChild(badge);

  document.body.appendChild(hit);
  el.setAttribute(DEBUG_MARKUP_ATTR, "");
  debugMarkupHits.push({ el, hit });
  debugMarkupHitMap.set(el, hit);
  return hit;
};

const flashDebugMarkupHit = (target) => {
  const hit = debugMarkupHitMap.get(target);
  if (!hit) return;

  const color =
    hit.style.getPropertyValue("--debug-markup-color").trim() || "#fff";
  const baseFill =
    hit.style.getPropertyValue("--debug-markup-fill").trim() ||
    hexToRgba(color, 0.2);

  debugMarkupFlashUntil = Date.now() + 1200;
  if (debugMarkupRefreshTimer != null) {
    window.clearTimeout(debugMarkupRefreshTimer);
    debugMarkupRefreshTimer = null;
  }
  if (debugMarkupRefreshFollowUpTimer != null) {
    window.clearTimeout(debugMarkupRefreshFollowUpTimer);
    debugMarkupRefreshFollowUpTimer = null;
  }

  const setOn = () => {
    hit.style.opacity = "1";
    hit.style.filter = "brightness(1.25)";
    hit.style.transform = "scale(1.015)";
    hit.style.background = hexToRgba(color, 0.34);
    hit.style.boxShadow = `0 0 0 4px ${hexToRgba(color, 0.35)}`;
  };

  const setOff = () => {
    hit.style.opacity = "0.85";
    hit.style.filter = "";
    hit.style.transform = "";
    hit.style.background = baseFill;
    hit.style.boxShadow = "";
  };

  setOff();

  let step = 0;
  const runStep = () => {
    if (!hit.isConnected) return;
    // on, off, on, off = 2 blinks
    if (step % 2 === 0) setOn();
    else setOff();
    step += 1;
    if (step < 4) {
      window.setTimeout(runStep, step % 2 === 0 ? 160 : 200);
    }
  };
  window.setTimeout(runStep, 0);
};

const createDebugMarkupListItem = ({
  tagLabel,
  tagColor,
  text,
  textColor,
  visible,
  target,
  indentPx = 6,
}) => {
  const item = document.createElement("li");
  const button = document.createElement("button");
  button.type = "button";
  button.className = visible
    ? "debug-markup-item"
    : "debug-markup-item is-dimmed";
  button.title = visible ? text : `${text} (화면에 보이지 않음)`;
  button.style.paddingLeft = `${indentPx}px`;

  const tag = document.createElement("span");
  tag.className = "debug-markup-tag";
  tag.style.color = tagColor;
  tag.textContent = tagLabel;

  const textEl = document.createElement("span");
  textEl.className = "debug-markup-text";
  textEl.textContent = text;
  if (textColor) textEl.style.color = textColor;

  button.append(tag, textEl);
  button.addEventListener("click", (event) => {
    event.stopPropagation();

    let flashed = false;
    const flashOnce = () => {
      if (flashed) return;
      flashed = true;
      syncDebugMarkupHits();
      if (visible) flashDebugMarkupHit(target);
    };

    const rect = target.getBoundingClientRect();
    const viewTop = 80;
    const viewBottom = window.innerHeight - 20;
    const needsScroll =
      rect.top < viewTop ||
      rect.bottom > viewBottom ||
      rect.top > window.innerHeight * 0.7 ||
      rect.bottom < window.innerHeight * 0.3;

    if (!needsScroll) {
      flashOnce();
      return;
    }

    target.scrollIntoView({ behavior: "smooth", block: "center" });
    runAfterScrollEnd(flashOnce);
    // nested scroll 등으로 scrollend가 없는 경우 대비
    window.setTimeout(flashOnce, 1500);
  });

  item.appendChild(button);
  return item;
};

const applyDebugMarkup = () => {
  debugMarkupApplying = true;
  const panelScrollTop = teardownDebugMarkupUI();

  try {
  const showHeadings = isDebugMarkupHeadingsEnabled();
  const showImages = isDebugMarkupImagesEnabled();

  const headings = Array.from(
    document.querySelectorAll(DEBUG_MARKUP_HEADING_SELECTOR),
  );
  const images = Array.from(
    document.querySelectorAll(DEBUG_MARKUP_IMAGE_SELECTOR),
  );
  const visibleHeadingCount = headings.filter(isDebugMarkupVisible).length;
  const visibleImageCount = images.filter(isDebugMarkupVisible).length;

  const styleEl = document.createElement("style");
  styleEl.id = DEBUG_MARKUP_STYLE_ID;
  styleEl.textContent = `
    #${DEBUG_MARKUP_VEIL_ID} {
      position: fixed;
      inset: 0;
      z-index: 2147483645;
      background: rgba(0, 0, 0, 0.28);
      pointer-events: none;
    }
    [${DEBUG_MARKUP_HIT_ATTR}] {
      position: fixed;
      z-index: 2147483646;
      box-sizing: border-box;
      margin: 0;
      padding: 0;
      border: 2px solid var(--debug-markup-color, #fff);
      background: var(--debug-markup-fill, rgba(255, 255, 255, 0.2));
      opacity: 0.85;
      pointer-events: none;
      transform-origin: center center;
      will-change: transform, opacity, filter, box-shadow;
    }
    [${DEBUG_MARKUP_HIT_ATTR}] .debug-markup-badge {
      position: absolute;
      top: 0;
      left: 0;
      transform: translateY(-100%);
      max-width: min(320px, 80vw);
      padding: 2px 6px;
      border-radius: 3px 3px 0 0;
      font: 700 11px/1.3 ui-monospace, SFMono-Regular, Menlo, monospace;
      color: #fff;
      background: var(--debug-markup-color, #333);
      white-space: normal;
      word-break: break-word;
      display: -webkit-box;
      -webkit-line-clamp: 3;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }
    #${DEBUG_MARKUP_PANEL_ID} {
      position: fixed;
      top: 12px;
      right: 12px;
      z-index: 2147483647;
      width: min(360px, calc(100vw - 24px));
      max-height: min(70vh, 560px);
      overflow: auto;
      margin: 0;
      padding: 12px 14px;
      border: 1px solid #222;
      border-radius: 8px;
      background: rgba(18, 18, 18, 0.94);
      color: #f5f5f5;
      font: 12px/1.45 ui-monospace, SFMono-Regular, Menlo, monospace;
      box-shadow: 0 8px 28px rgba(0, 0, 0, 0.35);
    }
    #${DEBUG_MARKUP_PANEL_ID} > header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
      margin-bottom: 10px;
      padding-bottom: 8px;
      border-bottom: 1px solid #444;
    }
    #${DEBUG_MARKUP_PANEL_ID} > header strong {
      font-size: 13px;
    }
    #${DEBUG_MARKUP_PANEL_ID} > header button {
      appearance: none;
      border: 0;
      border-radius: 4px;
      padding: 4px 10px;
      background: #c62828;
      color: #fff;
      cursor: pointer;
      font: inherit;
      font-weight: 700;
    }
    #${DEBUG_MARKUP_PANEL_ID} > header button:hover {
      background: #e53935;
    }
    #${DEBUG_MARKUP_PANEL_ID} .debug-markup-toggles {
      display: flex;
      flex-wrap: wrap;
      gap: 8px 12px;
      margin: 0 0 12px;
      padding-bottom: 10px;
      border-bottom: 1px solid #444;
    }
    #${DEBUG_MARKUP_PANEL_ID} .debug-markup-toggles label {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      cursor: pointer;
      user-select: none;
      font-weight: 700;
    }
    #${DEBUG_MARKUP_PANEL_ID} .debug-markup-toggles input {
      margin: 0;
      accent-color: #4caf50;
      cursor: pointer;
    }
    #${DEBUG_MARKUP_PANEL_ID} .debug-markup-section.is-off {
      opacity: 0.45;
    }
    #${DEBUG_MARKUP_PANEL_ID} .debug-markup-section.is-off .debug-markup-section-body {
      display: none;
    }
    #${DEBUG_MARKUP_PANEL_ID} .debug-markup-section + .debug-markup-section {
      margin-top: 14px;
      padding-top: 12px;
      border-top: 1px solid #444;
    }
    #${DEBUG_MARKUP_PANEL_ID} .debug-markup-section-title {
      margin: 0 0 8px;
      font-size: 12px;
      font-weight: 700;
      opacity: 0.85;
    }
    #${DEBUG_MARKUP_PANEL_ID} ol {
      margin: 0;
      padding: 0;
      list-style: none;
    }
    #${DEBUG_MARKUP_PANEL_ID} li {
      margin: 0 0 4px;
    }
    #${DEBUG_MARKUP_PANEL_ID} button.debug-markup-item {
      display: flex;
      gap: 8px;
      width: 100%;
      margin: 0;
      padding: 6px 8px;
      border: 0;
      border-radius: 4px;
      background: transparent;
      color: inherit;
      text-align: left;
      cursor: pointer;
      font: inherit;
    }
    #${DEBUG_MARKUP_PANEL_ID} button.debug-markup-item:hover {
      background: rgba(255, 255, 255, 0.08);
    }
    #${DEBUG_MARKUP_PANEL_ID} button.debug-markup-item.is-dimmed {
      opacity: 0.35;
    }
    #${DEBUG_MARKUP_PANEL_ID} button.debug-markup-item.is-dimmed:hover {
      opacity: 0.55;
      background: rgba(255, 255, 255, 0.06);
    }
    #${DEBUG_MARKUP_PANEL_ID} .debug-markup-tag {
      flex: 0 0 auto;
      min-width: 28px;
      font-weight: 700;
    }
    #${DEBUG_MARKUP_PANEL_ID} .debug-markup-text {
      flex: 1 1 auto;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      opacity: 0.9;
    }
    #${DEBUG_MARKUP_PANEL_ID} .debug-markup-empty {
      margin: 0;
      opacity: 0.7;
    }
  `;
  document.head.appendChild(styleEl);

  const veil = document.createElement("div");
  veil.id = DEBUG_MARKUP_VEIL_ID;
  veil.setAttribute("aria-hidden", "true");
  if (showHeadings || showImages) {
    document.body.appendChild(veil);
  }

  headings.forEach((heading) => {
    if (!showHeadings || !isDebugMarkupVisible(heading)) return;
    const level = Number(heading.tagName.slice(1));
    const color = DEBUG_MARKUP_LEVEL_COLORS[level] ?? "#333";
    createDebugMarkupHit(heading, heading.tagName.toLowerCase(), color);
  });

  images.forEach((img) => {
    if (!showImages || !isDebugMarkupVisible(img)) return;
    const altLabel = getImageAltLabel(img);
    const color = isImageAltIssue(img)
      ? DEBUG_MARKUP_IMAGE_MISSING_COLOR
      : DEBUG_MARKUP_IMAGE_COLOR;
    createDebugMarkupHit(img, truncateDebugLabel(`img: ${altLabel}`), color);
  });

  syncDebugMarkupHits();

  const panel = document.createElement("aside");
  panel.id = DEBUG_MARKUP_PANEL_ID;
  panel.setAttribute("aria-label", "Markup debug");

  const header = document.createElement("header");
  const title = document.createElement("strong");
  title.textContent = "Markup debug";
  const closeBtn = document.createElement("button");
  closeBtn.type = "button";
  closeBtn.textContent = "끄기";
  closeBtn.title = "debugMarkup 비활성화";
  closeBtn.addEventListener("click", () => {
    disableDebugMarkup();
  });
  header.append(title, closeBtn);
  panel.appendChild(header);

  const toggles = document.createElement("div");
  toggles.className = "debug-markup-toggles";

  const createToggle = (labelText, checked, onChange) => {
    const label = document.createElement("label");
    const input = document.createElement("input");
    input.type = "checkbox";
    input.checked = checked;
    input.addEventListener("click", (event) => {
      event.stopPropagation();
    });
    input.addEventListener("change", (event) => {
      event.stopPropagation();
      onChange(input.checked);
      applyDebugMarkup();
    });
    const text = document.createElement("span");
    text.textContent = labelText;
    label.append(input, text);
    return label;
  };

  toggles.append(
    createToggle("Headings", showHeadings, setDebugMarkupHeadingsEnabled),
    createToggle("Images alt", showImages, setDebugMarkupImagesEnabled),
  );
  panel.appendChild(toggles);

  const headingsSection = document.createElement("section");
  headingsSection.className = showHeadings
    ? "debug-markup-section"
    : "debug-markup-section is-off";
  const headingsTitle = document.createElement("p");
  headingsTitle.className = "debug-markup-section-title";
  headingsTitle.textContent =
    visibleHeadingCount === headings.length
      ? `Headings (${headings.length})`
      : `Headings (${visibleHeadingCount}/${headings.length})`;
  headingsSection.appendChild(headingsTitle);

  const headingsBody = document.createElement("div");
  headingsBody.className = "debug-markup-section-body";
  if (!headings.length) {
    const empty = document.createElement("p");
    empty.className = "debug-markup-empty";
    empty.textContent = "No heading tags found.";
    headingsBody.appendChild(empty);
  } else {
    const list = document.createElement("ol");
    headings.forEach((heading) => {
      const level = Number(heading.tagName.slice(1));
      list.appendChild(
        createDebugMarkupListItem({
          tagLabel: heading.tagName.toLowerCase(),
          tagColor: DEBUG_MARKUP_LEVEL_COLORS[level] ?? "#fff",
          text: heading.textContent?.trim() || "(empty)",
          visible: isDebugMarkupVisible(heading),
          target: heading,
          indentPx: 6 + (level - 1) * 12,
        }),
      );
    });
    headingsBody.appendChild(list);
  }
  headingsSection.appendChild(headingsBody);
  panel.appendChild(headingsSection);

  const imagesSection = document.createElement("section");
  imagesSection.className = showImages
    ? "debug-markup-section"
    : "debug-markup-section is-off";
  const imagesTitle = document.createElement("p");
  imagesTitle.className = "debug-markup-section-title";
  imagesTitle.textContent =
    visibleImageCount === images.length
      ? `Images (${images.length})`
      : `Images (${visibleImageCount}/${images.length})`;
  imagesSection.appendChild(imagesTitle);

  const imagesBody = document.createElement("div");
  imagesBody.className = "debug-markup-section-body";
  if (!images.length) {
    const empty = document.createElement("p");
    empty.className = "debug-markup-empty";
    empty.textContent = "No image tags found.";
    imagesBody.appendChild(empty);
  } else {
    const list = document.createElement("ol");
    images.forEach((img) => {
      const altIssue = isImageAltIssue(img);
      const altLabel = getImageAltLabel(img);
      const color = altIssue
        ? DEBUG_MARKUP_IMAGE_MISSING_COLOR
        : DEBUG_MARKUP_IMAGE_COLOR;
      list.appendChild(
        createDebugMarkupListItem({
          tagLabel: "img",
          tagColor: color,
          text: altLabel,
          textColor: altIssue ? DEBUG_MARKUP_IMAGE_MISSING_COLOR : undefined,
          visible: isDebugMarkupVisible(img),
          target: img,
        }),
      );
    });
    imagesBody.appendChild(list);
  }
  imagesSection.appendChild(imagesBody);
  panel.appendChild(imagesSection);

  document.body.appendChild(panel);
  panel.scrollTop = panelScrollTop;
  startDebugMarkupWatchers();
  return true;
  } finally {
    debugMarkupApplying = false;
  }
};

const debugMarkup = () => {
  if (isDebugMarkupEnabled() || document.getElementById(DEBUG_MARKUP_STYLE_ID)) {
    return disableDebugMarkup();
  }

  setDebugMarkupEnabled(true);
  return applyDebugMarkup();
};

const restoreDebugMarkupIfEnabled = () => {
  if (!isDebugMarkupEnabled()) return;
  if (!document.body) return;
  applyDebugMarkup();
};

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", restoreDebugMarkupIfEnabled, {
    once: true,
  });
} else {
  restoreDebugMarkupIfEnabled();
}

window.addEventListener("pageshow", restoreDebugMarkupIfEnabled);

window.debugMarkup = debugMarkup;

// URL hash anchor scroll
const getHashAnchorElement = (hash = window.location.hash) => {
  if (!hash || hash === "#") return null;

  let id = hash.startsWith("#") ? hash.slice(1) : hash;
  try {
    id = decodeURIComponent(id);
  } catch {
    // malformed % sequence — use raw id
  }
  if (!id) return null;

  return document.getElementById(id);
};

const scrollToUrlHash = ({
  hash = window.location.hash,
  behavior = "smooth",
  focusTarget = false,
} = {}) => {
  const anchorElement = getHashAnchorElement(hash);
  if (!anchorElement) return;

  scrollToAnchor({
    anchorElement,
    stickyHeader: stickyScroll.getStickyInner(anchorElement),
    focusTarget,
    behavior,
  });
};

const getNavigationType = () =>
  performance.getEntriesByType?.("navigation")?.[0]?.type ?? null;

const initUrlHashAnchorScroll = () => {
  if (!window.location.hash) return;

  const type = getNavigationType();
  if (type === "reload" || type === "back_forward") return;

  const jump = () => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        scrollToUrlHash();
      });
    });
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => jump("auto"), {
      once: true,
    });
  } else if (document.readyState !== "complete") {
    jump("auto");
  }

  const settle = () => {
    jump("smooth");
    window.setTimeout(() => jump("auto"), 400);
  };

  if (document.readyState === "complete") {
    settle();
    return;
  }

  window.addEventListener("load", settle, { once: true });
};

initUrlHashAnchorScroll();

if (window.navigation) {
  window.navigation.addEventListener("navigate", (event) => {
    if (event.navigationType === "traverse") return;
    if (!event.destination.sameDocument) return;
    if (!event.canIntercept || event.downloadRequest || event.formData) return;

    const dest = new URL(event.destination.url, document.baseURI);
    if (!dest.hash || dest.hash === "#") return;

    event.intercept({
      scroll: "manual",
      handler() {
        scrollToUrlHash({
          hash: dest.hash
        });
      },
    });
  });
}